/**
 * Navigator component registry
 *
 * Every visual building-block for navigator scripts lives here.
 * All components extend NavBaseComponent to get `@Input() node`.
 *
 * Responsive behaviour:
 *   - Mobile-first. Full screen on phones and every other touch device (max-width 720px inside).
 *   - Mouse users on a wide screen: max-width 520px, centred with a card shadow.
 *   - The page shell (.nav-page) enforces this; all components inherit 100% width within it.
 */
import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, Directive, Inject, Optional, forwardRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store, createSelector } from '@ngrx/store';
import { QuillViewComponent } from 'ngx-quill';
import QRCode from 'qrcode';
import { Observable, Subscription, distinctUntilChanged, filter } from 'rxjs';
import { AddTagsToObjectsGQL, GetConfigValuesGQL, TagsGQL } from 'src/generated/graphql.generated';

import { OnlineStatusService } from '../../../online-status.service';
import { quickJsActions } from '../../../quickjs/quickjs.actions';
import { selectNavigatorActiveEventId, selectNavigatorCurrentStepId, selectNavigatorOpsManagers, selectNavigatorOpsManagersStale, selectNavigatorStepStartTime } from '../../../quickjs/quickjs.feature';
import { formatPhoneNumber } from '../../../shared/phone.pipe';
import { addressContainsCity, formatAddressWithCity } from '../../../utilities/locations.util';
import { jobCreateSummaryFeature } from '../../job-create/job-create-summary/job-create-summary.feature';
import { SummaryType } from '../../job-create/job-create-summary/job-create-summary.reducer';
import { StepTiming } from '../../job-helpers';
import { JobSummary, selectJob, selectJobId } from '../../job-tool.reducer';
import { WorkOrdersActions } from '../../job-workorders/job-workorders.actions';

import { NavCollectPaymentComponent } from './nav/nav-collect-payment.component';
import { NavCrewRollCallComponent } from './nav/nav-crew-roll-call.component';
import { NavCustomerSignatureComponent } from './nav/nav-customer-signature.component';
import { NavInventoryEditorComponent as NavInventoryEditorFromNav } from './nav/nav-inventory-editor.component';
import { NavInventorySummaryComponent } from './nav/nav-inventory-summary.component';
import { NavLoadProgressComponent } from './nav/nav-load-progress.component';
import { NavPandaDocSignerComponent } from './nav/nav-pandadoc-signer.component';
import { NavPhotoCaptureComponent as NavPhotoCaptureFromNav } from './nav/nav-photo-capture.component';
import { NavStepGateService } from './nav/nav-step-gate.service';
import { NavStepTimerDetailComponent, NavStepTimerToggleComponent } from './nav-step-timer-bar.component';
import { NavNode } from './navigator-node.component';

// Public exports for use in navigator-node.component.ts
export { NavCustomerSignatureComponent } from './nav/nav-customer-signature.component';
export { NavLoadProgressComponent } from './nav/nav-load-progress.component';
export { NavPhotoCaptureComponent } from './nav/nav-photo-capture.component';
export { NavPandaDocSignerComponent } from './nav/nav-pandadoc-signer.component';
export { NavInventoryEditorComponent } from './nav/nav-inventory-editor.component';
export { NavInventorySummaryComponent } from './nav/nav-inventory-summary.component';
export { NavCollectPaymentComponent } from './nav/nav-collect-payment.component';

// ─── Base ─────────────────────────────────────────────────────────────────────

/**
 * Shared base for all navigator components.
 * Provides `@Input() node: NavNode`. Extend this instead of redeclaring the input.
 */
@Directive()
export abstract class NavBaseComponent {
    @Input() node!: NavNode;
}

// ─────────────────────────────────────────────────────────────────────────────
// LEAF COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

// ─── CustomerInfo ─────────────────────────────────────────────────────────────

@Component({
    selector: 'app-nav-customer-info',
    standalone: true,
    imports: [CommonModule],
    styles: [`
        .info-block {
            text-align: center;
            margin-bottom: 1.5rem;
        }
        .icon-circle {
            width: 56px; height: 56px; border-radius: 50%;
            background: #e8edf2;
            display: flex; align-items: center; justify-content: center;
            margin: 0 auto 0.75rem;
        }
        .icon-circle i { font-size: 1.5rem; color: #5a7a9a; }
        .customer-name { font-size: 1.2rem; font-weight: 700; margin-bottom: 0.3rem; color: #1a1a1a; }
        .customer-detail { font-size: 1rem; color: #555; line-height: 1.8; }
        /* The phone is a link, not text: colour it like one and pad it to a 44px tap
           target -- line-height alone leaves the row at ~29px, too small on a phone. */
        .customer-phone {
            display: inline-block;
            box-sizing: border-box;
            min-height: 44px;
            padding: 0.5rem 0.75rem;
            color: #2d6a2d;
            font-weight: 600;
            text-decoration: none;
        }
        .locations { text-align: center; margin-bottom: 1.5rem; }
        .location-line { font-size: 1rem; color: #222; font-weight: 500; }
        .location-arrow { color: #2d6a2d; margin: 0.35rem 0; font-size: 1.3rem; }
        /* Same treatment as the phone above (#78): the address is a link, so colour it like
           one and pad it to a 44px tap target. font-size 1rem alone leaves the row too short
           to hit reliably one-handed. */
        .location-link {
            display: inline-flex; align-items: center; gap: 0.4rem;
            box-sizing: border-box;
            min-height: 44px;
            padding: 0.5rem 0.75rem;
            color: #2d6a2d; font-weight: 600; text-decoration: none;
        }
        .location-link i { font-size: 0.95rem; }
    `],
    template: `
        <div class="info-block" *ngIf="node.props?.customerName || node.props?.phone">
            <div class="icon-circle"><i class="pi pi-user"></i></div>
            <div class="customer-name" *ngIf="node.props?.customerName">{{ node.props.customerName }}</div>
            <a class="customer-detail customer-phone" *ngIf="node.props?.phone"
               [href]="'tel:' + node.props.phone">{{ formattedPhone }}</a>
        </div>
        <div class="locations" *ngIf="locationRows.length">
            <ng-container *ngFor="let row of locationRows; let last = last; trackBy: trackRow">
                <a *ngIf="row.url" class="location-line location-link"
                   [href]="row.url" target="_blank" rel="noopener">
                    <i class="pi pi-map-marker"></i>{{ row.text }}
                </a>
                <div class="location-line" *ngIf="!row.url">{{ row.text }}</div>
                <div class="location-arrow" *ngIf="!last">↓</div>
            </ng-container>
        </div>
    `,
})
export class NavCustomerInfoComponent extends NavBaseComponent {
    /**
     * Display the number the way the rest of the app does -- the script hands us
     * `customer.phone` raw, so without this the crew sees `19135551234` where the job
     * page shows `+1 913 555-1234`.
     *
     * Only the *text* is formatted. The `href` keeps the raw value: `tel:` wants
     * unambiguous digits, and `formatPhoneNumber` inserts spaces and a `+`. It returns
     * its input unchanged for anything it does not recognise (extensions, already
     * formatted numbers), so an odd value degrades to today's behaviour rather than
     * being mangled.
     */
    get formattedPhone(): string {
        const phone = this.node?.props?.phone;
        return phone ? formatPhoneNumber(String(phone), true) : '';
    }

    /**
     * The step's two addresses, each with a maps link (**P15**).
     *
     * Step 1 showed both addresses as dead text while the phone directly above them was
     * tappable (**#78**) -- and this is the step whose own help text says *"Make sure you know
     * where you're going."* The maps *button* already in this file belongs to `LocationInfo`,
     * which only the three drive steps use, so the crew could open maps once they were already
     * driving but not at the dock.
     *
     * A link, not the embedded map view: that is **#77**, which is a different item and is held
     * on a Maps API cost check. An anchor costs nothing and works offline in the sense that
     * matters -- the crew's own maps app takes over from there.
     *
     * The URL is built exactly as `LocationInfo.mapsUrl` builds it, deliberately, so the two
     * surfaces cannot drift. A location with no address at all still renders its text (as
     * "Unknown", the previous behaviour) but without a link, since there would be nothing to
     * search for.
     */
    /**
     * Belt and braces with the cache below: the two addresses keep their position for the life of
     * a step, so tracking by index holds the DOM node still even if the cache is ever bypassed.
     */
    trackRow(index: number): number { return index; }

    private _rowsCache: { src: unknown; rows: { text: string; url: string | null }[] } | null = null;

    get locationRows(): { text: string; url: string | null }[] {
        const locations = this.node?.props?.locations ?? [];

        // Cached on the source array's IDENTITY, and that is load-bearing rather than an
        // optimisation. This getter is the collection in an `*ngFor`, so Angular re-reads it on
        // every change-detection cycle; returning a freshly-built array of fresh objects each
        // time makes the default identity-based trackBy see all-new items and **destroy and
        // rebuild the anchors on every cycle**. The step header runs a one-second timer, so they
        // were being replaced several times a second — fast enough that a click never survived
        // from mousedown to mouseup, and the maps links silently did nothing. They rendered
        // correctly and carried the right href throughout, which is what made it hard to see;
        // the tell was the nodes flashing in the Elements panel.
        //
        // `props` is rebuilt only when the script re-runs, so identity is a sound cache key and
        // a changed location still refreshes.
        if (this._rowsCache && this._rowsCache.src === locations) { return this._rowsCache.rows; }

        const rows = locations.filter(Boolean).map((loc: any) => {
            const address = loc.addressLineOne || loc.address;
            const query = formatAddressWithCity(address, loc.city);
            return {
                text: query || 'Unknown',
                url: address
                    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
                    : null,
            };
        });

        this._rowsCache = { src: locations, rows };
        return rows;
    }
}

// ─── LocationInfo ──────────────────────────────────────────────────────────────

@Component({
    selector: 'app-nav-location-info',
    standalone: true,
    imports: [CommonModule],
    styles: [`
        .location-card {
            background: white;
            border-radius: 10px;
            padding: 1.1rem 1.2rem;
            margin-bottom: 1rem;
            box-shadow: 0 1px 4px rgba(0,0,0,0.07);
        }
        .address-row {
            display: flex; align-items: flex-start; gap: 0.75rem;
            margin-bottom: 0.85rem;
        }
        .address-row i { font-size: 1.3rem; color: #2d6a2d; margin-top: 0.1rem; flex-shrink: 0; }
        .address-text { font-size: 1.05rem; font-weight: 600; color: #1a1a1a; line-height: 1.4; }
        .address-city { font-size: 0.9rem; color: #666; margin-top: 0.15rem; }
        .maps-btn {
            display: flex; align-items: center; justify-content: center; gap: 0.5rem;
            width: 100%; padding: 0.85rem;
            background: #2d6a2d; color: white;
            border: none; border-radius: 8px;
            font-size: 1rem; font-weight: 600; cursor: pointer;
            text-decoration: none; margin-bottom: 0.5rem;
        }
        .maps-btn:active { background: #1f4d1f; }
        .travel-row {
            display: flex; align-items: center; gap: 0.5rem;
            font-size: 0.9rem; color: #666; margin-top: 0.75rem;
        }
        .travel-row i { color: #2d6a2d; }
        .eta { font-weight: 600; color: #1a1a1a; }
    `],
    template: `
        <div class="location-card">
            <div class="address-row" *ngIf="node.props?.address || node.props?.addressLineOne">
                <i class="pi pi-map-marker"></i>
                <div>
                    <div class="address-text">{{ node.props?.addressLineOne || node.props?.address }}</div>
                    <div class="address-city" *ngIf="cityLine">{{ cityLine }}</div>
                </div>
            </div>

            <a *ngIf="mapsUrl" [href]="mapsUrl" target="_blank" class="maps-btn">
                <i class="pi pi-map"></i> Open in Maps
            </a>

            <div class="travel-row" *ngIf="node.props?.travelTime">
                <i class="pi pi-car"></i>
                <span>Travel time: <span class="eta">{{ travelMinutes }} min</span></span>
                <span *ngIf="etaTime" style="margin-left:auto;color:#1a1a1a;font-weight:600">ETA {{ etaTime }}</span>
            </div>
        </div>
    `,
})
export class NavLocationInfoComponent extends NavBaseComponent {
    get travelMinutes(): number {
        return Math.round((this.node.props?.travelTime ?? 0) / 60);
    }
    /** The city, suppressed when the address line above already ends with it. */
    get cityLine(): string | null {
        const addr = this.node.props?.addressLineOne || this.node.props?.address;
        const city = this.node.props?.city;
        if (!city) { return null; }
        return addressContainsCity(addr, city) ? null : city;
    }

    get mapsUrl(): string | null {
        const addr = this.node.props?.addressLineOne || this.node.props?.address;
        if (!addr) return null;
        // Same string CustomerInfo builds, on purpose: the two surfaces must not drift.
        const query = formatAddressWithCity(addr, this.node.props?.city);
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
    }
    get etaTime(): string | null {
        const arrival = this.node.props?.arrivalTime;
        if (!arrival) return null;
        const d = new Date(arrival * 1000);
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
}

// ─── Checkbox ─────────────────────────────────────────────────────────────────

@Component({
    selector: 'app-nav-checkbox',
    standalone: true,
    imports: [CommonModule],
    styles: [`
        .checkbox-row {
            display: flex; align-items: center; gap: 1rem;
            padding: 1rem 0; border-bottom: 1px solid #eee;
            cursor: pointer; user-select: none; -webkit-user-select: none;
        }
        .checkbox-row:last-child { border-bottom: none; }
        .checkbox-row:active { background: #f9f9f9; margin: 0 -0.5rem; padding-left: 0.5rem; padding-right: 0.5rem; border-radius: 6px; }
        .check-box {
            width: 1.7rem; height: 1.7rem; flex-shrink: 0;
            border: 2px solid #ccc; border-radius: 6px;
            display: flex; align-items: center; justify-content: center;
            transition: all 0.15s;
        }
        .check-box.checked { background: #2d6a2d; border-color: #2d6a2d; }
        .check-box i { color: white; font-size: 1rem; }
        .label-wrap { flex: 1; }
        .label-text { font-size: 1.05rem; color: #222; line-height: 1.4; }
        .subtitle { display: block; font-size: 0.85rem; color: #888; margin-top: 0.2rem; }
    `],
    template: `
        <div class="checkbox-row" (click)="toggle()">
            <div class="check-box" [class.checked]="checked">
                <i class="pi pi-check" *ngIf="checked"></i>
            </div>
            <div class="label-wrap">
                <span class="label-text">{{ node.props?.title }}</span>
                <span class="subtitle" *ngIf="node.props?.subtitle">{{ node.props.subtitle }}</span>
            </div>
        </div>
    `,
})
export class NavCheckboxComponent extends NavBaseComponent implements OnInit {
    checked = false;

    ngOnInit() {
        // Report the starting state, so the step is blocked from the moment it mounts rather
        // than only after the first tap. Also matters on a return via undo (#25), where the
        // component is rebuilt with `checked` back to false.
        this._notifyGate();
    }

    toggle() {
        this.checked = !this.checked;
        this._notifyGate();
    }

    private _notifyGate() {
        if (typeof this.node.props?.onReady === 'function') {
            this.node.props.onReady(this.checked);
        }
    }
}

// ─── InventoryEditor — nav/nav-inventory-editor.component.ts ──────────────────
// Re-exported below via top-level import

// ─── CustomerSignature — nav/nav-customer-signature.component.ts ──────────────
// Re-exported below via top-level import

// ─── PandaDocSigner — nav/nav-pandadoc-signer.component.ts ───────────────────
// Re-exported below via top-level import

// ─── LocationPermission ───────────────────────────────────────────────────────

/**
 * Shown on the first step to request geolocation permissions.
 * Displays the current state: granted / denied / requesting / not-supported.
 * Non-blocking — crews can proceed even if they deny permission.
 */
@Component({
    selector: 'app-nav-location-permission',
    standalone: true,
    imports: [CommonModule],
    styles: [`
        :host { display: block; margin-bottom: 1rem; }
        .perm-card {
            border-radius: 10px; padding: 0.85rem 1.1rem;
            display: flex; align-items: flex-start; gap: 0.75rem;
            font-size: 0.88rem; line-height: 1.5;
        }
        .perm-card.granted  { background: #f0f7f0; border: 1px solid #c6e2c6; }
        .perm-card.denied   { background: #fff7ed; border: 1px solid #fed7aa; }
        .perm-card.prompt   { background: #f0f7ff; border: 1px solid #bfdbfe; }
        .perm-card.unknown  { background: #f5f5f5; border: 1px solid #e0e0e0; }
        .perm-icon { font-size: 1.2rem; margin-top: 0.05rem; flex-shrink: 0; }
        .icon-granted { color: #2d6a2d; }
        .icon-denied  { color: #d97706; }
        .icon-prompt  { color: #1d4ed8; }
        .icon-unknown { color: #888; }
        .perm-text strong { display: block; font-weight: 700; margin-bottom: 0.2rem; }
        .perm-btn {
            margin-top: 0.6rem; padding: 0.5rem 1rem;
            background: #1d4ed8; color: white; border: none; border-radius: 8px;
            font-size: 0.85rem; font-weight: 600; cursor: pointer; font-family: inherit;
        }
        .perm-btn:active { background: #1e40af; }
    `],
    template: `
        <div class="perm-card" [class]="statusClass">
            <i class="perm-icon" [class]="iconClass"></i>
            <div class="perm-text">
                <ng-container *ngIf="status === 'granted'">
                    <strong>Location access granted ✓</strong>
                    Your location is only recorded when you tap Next at each step — not continuously.
                    It is only visible to dispatch while you are on site.
                </ng-container>
                <ng-container *ngIf="status === 'denied'">
                    <strong>Location access denied</strong>
                    The dispatch team won't see your position. You can still use the navigator normally.
                    To enable later, update your browser's location settings.
                </ng-container>
                <ng-container *ngIf="status === 'prompt'">
                    <strong>Allow location access?</strong>
                    This lets dispatch see your position at each step during the move.
                    Your location is only captured when you tap Next — not tracked continuously.
                    It is only visible to dispatch while you are on site.
                    <br>
                    <button class="perm-btn" (click)="requestPermission()">
                        <i class="pi pi-map-marker"></i> Allow Location Access
                    </button>
                </ng-container>
                <ng-container *ngIf="status === 'unsupported'">
                    <strong>Location not supported</strong>
                    This device doesn't support location. Dispatch tracking won't be available.
                </ng-container>
                <ng-container *ngIf="status === 'checking'">
                    <strong>Checking location permissions...</strong>
                </ng-container>
            </div>
        </div>
    `,
})
export class NavLocationPermissionComponent extends NavBaseComponent implements OnInit {
    status: 'checking' | 'granted' | 'denied' | 'prompt' | 'unsupported' = 'checking';

    get statusClass(): string {
        return {
            checking: 'unknown', granted: 'granted', denied: 'denied',
            prompt: 'prompt', unsupported: 'unknown',
        }[this.status] ?? 'unknown';
    }
    get iconClass(): string {
        return {
            checking: 'pi pi-spin pi-spinner icon-unknown',
            granted:  'pi pi-map-marker icon-granted',
            denied:   'pi pi-exclamation-triangle icon-denied',
            prompt:   'pi pi-map-marker icon-prompt',
            unsupported: 'pi pi-times-circle icon-unknown',
        }[this.status] ?? 'pi pi-question icon-unknown';
    }

    ngOnInit() {
        if (!navigator.geolocation) { this.status = 'unsupported'; return; }
        if (!navigator.permissions) { this.status = 'prompt'; return; }

        navigator.permissions.query({ name: 'geolocation' }).then(result => {
            this.status = result.state as 'granted' | 'denied' | 'prompt';
            result.onchange = () => { this.status = result.state as 'granted' | 'denied' | 'prompt'; };
        }).catch(() => { this.status = 'prompt'; });
    }

    requestPermission() {
        navigator.geolocation.getCurrentPosition(
            () => { this.status = 'granted'; },
            () => { this.status = 'denied'; },
            { timeout: 10000 },
        );
        this.status = 'checking';
    }
}

// ─── JobNotes ──────────────────────────────────────────────────────────────────

/**
 * Reads one summary out of the job-tool editor state and shapes it for this component.
 *
 * A real memoized selector, not a `store.select(fn)` projector: the projector builds a new
 * object on every call, and `store.select` dedupes by reference, so it re-emitted on every
 * action dispatched anywhere in the app. That pushed a fresh value into the Quill editor
 * dozens of times a second while the crew was typing in it (when this was an editor).
 *
 * Parameterised by summary key and fallback text, both fixed for the life of the node, so
 * each component instance builds its selector once in `ngOnInit`.
 */
const selectNavJobNotesView = (key: SummaryType, fallbackText: string) => createSelector(
    jobCreateSummaryFeature.selectSummaryEditors,
    (summaryEditors): JobSummary => {

        const delta = summaryEditors?.[key] as any;
        // delta is a Quill Delta — check if it has any ops with content
        const ops = delta?.ops ?? [];
        const hasContent = ops.some((op: any) => typeof op.insert === 'string' && op.insert.trim());

        if (hasContent) {
            return { summary: delta, format: 'Object', isEmpty: false };
        }

        // Fallback: plain text from input.jobNotes passed by the page component
        return {
            summary: fallbackText,
            format: 'text',
            isEmpty: !fallbackText.trim(),
        };
    },
);

/**
 * Shows a job summary (crewSummary, adminSummary, or customerSummary) from the
 * NgRx store. Read-only (#76): the office edits these notes on the job page; the crew reports
 * through step comments (#73).
 */
@Component({
    selector: 'app-nav-job-notes',
    standalone: true,
    imports: [CommonModule, QuillViewComponent],
    styles: [`
        :host { display: block; margin-bottom: 1rem; }
        /* Both note blocks are the same white card the rest of the navigator uses. They are told
           apart by their titles, not by colour — a second palette here
           read as a different kind of thing rather than a second note. */
        .note-card {
            background: white; border: 1px solid #e4e4e4;
            box-shadow: 0 1px 4px rgba(0,0,0,0.07);
            border-radius: 10px; padding: 0.85rem 1rem; margin-bottom: 0.9rem;
        }
        .note-card:last-child { margin-bottom: 0; }
        .note-card-header {
            display: flex; align-items: center; justify-content: space-between;
            gap: 0.5rem; margin-bottom: 0.4rem;
        }
        .note-card-title {
            font-size: 0.8rem; font-weight: 700; color: #555;
            text-transform: uppercase; letter-spacing: 0.05em;
        }
        .note-card-body { font-size: 0.95rem; color: #333; line-height: 1.6; }
        .empty-hint { font-size: 0.9rem; color: #aaa; font-style: italic; }
        .note-card.office .note-card-title { margin-bottom: 0.4rem; }
        /* Quill's own sheet pads .ql-editor by 12px 15px, which indented rich-text notes against
           their own card title while a plain-text note sat flush. Forced rather than out-specified:
           the rule has to beat whatever Quill ships next, and the two notes have to line up with
           each other whichever format each happens to be stored in. */
        ::ng-deep quill-view .ql-container { border: none !important; }
        ::ng-deep quill-view .ql-editor {
            padding: 0 !important;
            font-size: 0.95rem; line-height: 1.6; color: #333;
        }
        ::ng-deep quill-view .ql-editor > *:first-child { margin-top: 0; }
        ::ng-deep quill-view .ql-editor > *:last-child { margin-bottom: 0; }
    `],
    template: `
        <!-- Requirement: [6750] - The office's notes for this event, read-only, above the crew's own.
             Kept a separate card rather than merged into the crew notes: different source,
             different owner. Titled "Event Notes"
             because that is what the office calls the field it types them into on the dispatch
             board — the crew and the office should not have two names for one thing. -->
        <div class="note-card office" *ngIf="officeNotes$ | async as office">
            <div class="note-card-title">Event Notes</div>
            <p class="note-card-body" *ngIf="office.format !== 'Object'"
               style="white-space:pre-line;margin:0;">{{ office.content }}</p>
            <quill-view class="note-card-body" *ngIf="office.format === 'Object'"
                [content]="office.content"
                format="object"
                theme="snow"
                [modules]="{ toolbar: false }">
            </quill-view>
        </div>

        <div class="note-card crew">
            <div class="note-card-header">
                <span class="note-card-title">{{ label }}</span>
            </div>

            <ng-container *ngIf="summary$ | async as summary">
                <span class="empty-hint" *ngIf="summary.isEmpty">No {{ label | lowercase }}.</span>
                <!-- Plain text fallback (from input.jobNotes) -->
                <p class="note-card-body" *ngIf="!summary.isEmpty && summary.format !== 'Object'"
                   style="white-space:pre-line;margin:0;">
                    {{ summary.summary }}
                </p>
                <!-- Quill rich text (from store) -->
                <quill-view class="note-card-body" *ngIf="!summary.isEmpty && summary.format === 'Object'"
                    [content]="summary.summary"
                    format="object"
                    theme="snow"
                    [modules]="{ toolbar: false }">
                </quill-view>
            </ng-container>
        </div>
    `,
})
export class NavJobNotesComponent extends NavBaseComponent implements OnInit {
    summary$!: Observable<JobSummary>;

    /**
     * The office's notes for this event (`metadata.eventNotes`), or null when there are none.
     *
     * The value is a serialised Quill delta on events saved since the notes feature landed, and
     * plain text on older ones — so `format` mirrors what the crew-notes view below already does
     * rather than assuming a delta and rendering nothing on a legacy note.
     */
    readonly officeNotes$: Observable<{ content: any; format: 'Object' | 'Text' } | null> =
        this.store.select(createSelector(
            selectJob,
            selectNavigatorActiveEventId,
            (job, eventId) => {
                const raw = (job?.events ?? []).find((e) => e.id === eventId)?.metadata?.eventNotes;
                if (!raw || typeof raw !== 'string' || !raw.trim()) { return null; }

                try {
                    const parsed = JSON.parse(raw);
                    // A delta is `{ ops: [...] }`. Anything else that happens to be valid JSON is
                    // treated as text, and an empty delta renders nothing.
                    //
                    // The ops check has to gate on the array being *present*, not on it being
                    // non-empty: a legacy plain-text note that happens to parse as JSON — a bare
                    // gate code like `123`, a unit number — took the empty-delta path and
                    // disappeared from the step with no error.
                    if (Array.isArray(parsed?.ops)) {
                        const hasText = parsed.ops.some((op: any) =>
                            typeof op?.insert === 'string' && op.insert.trim());
                        return hasText ? { content: parsed, format: 'Object' as const } : null;
                    }
                    return { content: raw, format: 'Text' as const };
                } catch {
                    return { content: raw, format: 'Text' as const };
                }
            },
        ));

    get summaryKey(): 'crewSummary' | 'adminSummary' | 'customerSummary' {
        const k = this.node?.props?.summaryKey;
        return (k === 'adminSummary' || k === 'customerSummary') ? k : 'crewSummary';
    }
    get label(): string {
        return { crewSummary: 'Crew Notes', adminSummary: 'Admin Notes', customerSummary: 'Customer Notes' }[this.summaryKey];
    }

    constructor(private store: Store) { super(); }

    ngOnInit() {
        // Fallback text is fixed for the life of the node, so it is captured into the
        // selector rather than read from `this` inside the projector.
        const fallbackText: string = (this.node?.props?.jobNotes ?? this.node?.props?.eventNotes ?? '') as string;
        this.summary$ = this.store.select(selectNavJobNotesView(this.summaryKey, fallbackText));
    }
}

// ─── HomeProtection ───────────────────────────────────────────────────────────

@Component({
    selector: 'app-nav-home-protection',
    standalone: true,
    imports: [CommonModule],
    styles: [`
        :host { display: block; margin-bottom: 1rem; }
        .protection-card {
            background: white; border-radius: 10px;
            box-shadow: 0 1px 4px rgba(0,0,0,0.07);
            overflow: hidden;
        }
        .protection-header {
            background: #f0f7f0; padding: 0.75rem 1.1rem;
            display: flex; align-items: center; gap: 0.5rem;
            border-bottom: 1px solid #e0ede0;
        }
        .protection-header i { color: #2d6a2d; font-size: 1rem; }
        .protection-header span { font-size: 0.85rem; font-weight: 700; color: #2d6a2d; text-transform: uppercase; letter-spacing: 0.05em; }
        .item-row {
            display: flex; align-items: center; gap: 1rem;
            padding: 1rem 1.1rem; border-bottom: 1px solid #f0f0f0;
            cursor: pointer; user-select: none; -webkit-user-select: none;
        }
        .item-row:last-child { border-bottom: none; }
        .item-row:active { background: #f9f9f9; }
        .check-box {
            width: 1.7rem; height: 1.7rem; flex-shrink: 0;
            border: 2px solid #ccc; border-radius: 6px;
            display: flex; align-items: center; justify-content: center;
            transition: all 0.15s;
        }
        .check-box.checked { background: #2d6a2d; border-color: #2d6a2d; }
        .check-box i { color: white; font-size: 1rem; }
        .item-label { flex: 1; }
        .item-title { font-size: 1.05rem; font-weight: 600; color: #1a1a1a; }
        .item-note { font-size: 0.85rem; color: #888; margin-top: 0.15rem; }
        .all-done {
            background: #dcfce7; padding: 0.6rem 1.1rem;
            display: flex; align-items: center; gap: 0.5rem;
            font-size: 0.9rem; font-weight: 600; color: #15803d;
        }
    `],
    template: `
        <div class="protection-card">
            <div class="protection-header">
                <i class="pi pi-shield"></i>
                <span>Home Protection Checklist</span>
            </div>
            <div class="item-row" *ngFor="let item of items; let i = index" (click)="toggle(i)">
                <div class="check-box" [class.checked]="item.checked">
                    <i class="pi pi-check" *ngIf="item.checked"></i>
                </div>
                <div class="item-label">
                    <div class="item-title">{{ item.text }}</div>
                    <div class="item-note" *ngIf="item.note">{{ item.note }}</div>
                </div>
            </div>
            <div class="all-done" *ngIf="allChecked">
                <i class="pi pi-check-circle"></i> All protection in place — ready to move!
            </div>
        </div>
    `,
})
export class NavHomeProtectionComponent extends NavBaseComponent implements OnInit {
    items: Array<{ id: string; text: string; note?: string; checked: boolean }> = [];

    get allChecked(): boolean { return this.items.length > 0 && this.items.every(i => i.checked); }

    ngOnInit() {
        const raw = this.node.props?.items ?? [];
        this.items = raw.map((item: any) => ({ ...item, checked: false }));
        // If no items configured, use the standard three
        if (this.items.length === 0) {
            this.items = [
                { id: 'doors',    text: 'Doors',    note: 'Protective covers on all main doorways',   checked: false },
                { id: 'floors',   text: 'Floors',   note: 'Floor runners in all high-traffic areas',  checked: false },
                { id: 'railings', text: 'Railings', note: 'Wrap banisters with protective padding',   checked: false },
            ];
        }

        // Report the starting state rather than relying on the gate's registered default.
        // Correct either way here — the fallback above means the list is never empty — but
        // every gated component now reports on init, so none of them can be the next one
        // that silently blocks a step. See #19.
        this._notifyGate();
    }

    toggle(i: number) {
        this.items[i].checked = !this.items[i].checked;
        this._notifyGate();
    }

    private _notifyGate() {
        if (typeof this.node.props?.onReady === 'function') {
            this.node.props.onReady(this.allChecked);
        }
    }
}

// ─── StarRating ───────────────────────────────────────────────────────────────

@Component({
    selector: 'app-nav-star-rating',
    standalone: true,
    imports: [CommonModule, FormsModule],
    styles: [`
        :host { display: block; margin-bottom: 1rem; }
        .rating-card {
            background: white; border-radius: 10px;
            padding: 1.5rem 1.2rem; text-align: center;
            box-shadow: 0 1px 4px rgba(0,0,0,0.07);
        }
        .rating-title { font-size: 1.2rem; font-weight: 700; color: #1a1a1a; margin-bottom: 0.3rem; }
        .rating-sub { font-size: 0.9rem; color: #666; margin-bottom: 1.25rem; }
        .stars { display: flex; justify-content: center; gap: 0.5rem; margin-bottom: 1rem; }
        .star { font-size: 2.5rem; cursor: pointer; transition: transform 0.1s; color: #e0e0e0; }
        .star.active { color: #f59e0b; }
        .star:active { transform: scale(1.2); }
        .feedback-area {
            width: 100%; box-sizing: border-box;
            border: 1.5px solid #ced4da; border-radius: 8px;
            padding: 0.85rem; font-size: 1rem; font-family: inherit;
            resize: none; margin-bottom: 0.75rem;
        }
        .feedback-area:focus { outline: none; border-color: #2d6a2d; }
        .escalate-card {
            background: #fff7ed; border: 1px solid #fed7aa;
            border-radius: 8px; padding: 1rem; text-align: left;
            margin-top: 0.5rem;
        }
        .escalate-title { font-size: 0.95rem; font-weight: 700; color: #c2410c; margin-bottom: 0.35rem; }
        .escalate-text { font-size: 0.9rem; color: #7c2d12; margin-bottom: 0.75rem; line-height: 1.5; }
        .escalate-btn {
            display: flex; align-items: center; gap: 0.5rem;
            padding: 0.75rem 1rem; background: #ea580c; color: white;
            border: none; border-radius: 8px; font-size: 0.95rem; font-weight: 600;
            cursor: pointer; width: 100%; justify-content: center;
            box-sizing: border-box; min-height: 44px; text-decoration: none; font-family: inherit;
        }
        .escalate-btn + .escalate-btn { margin-top: 0.5rem; }
        .call-list-title { font-size: 0.85rem; font-weight: 700; color: #7c2d12; margin: 0.75rem 0 0.4rem; }
        .review-qr { margin-top: 1rem; }
        .review-qr img { width: 220px; height: 220px; display: block; margin: 0 auto; }
        .review-qr-caption { font-size: 0.9rem; color: #555; margin-top: 0.5rem; }
    `],
    template: `
        <div class="rating-card">
            <div class="rating-title">{{ node.props?.title || 'How did we do today?' }}</div>
            <div class="rating-sub">{{ node.props?.subtitle || 'Ask the customer for an honest rating' }}</div>

            <div class="stars">
                <span *ngFor="let s of [1,2,3,4,5]" class="star"
                      [class.active]="s <= rating"
                      (click)="setRating(s)">★</span>
            </div>

            <textarea *ngIf="belowThreshold"
                class="feedback-area" rows="3"
                placeholder="What could we have done better?"
                [(ngModel)]="feedback"
                (blur)="onFeedbackBlur()">
            </textarea>

            <div class="escalate-card" *ngIf="belowThreshold">
                <div class="escalate-title">Not {{ threshold }} stars — resolve this now</div>
                <div class="escalate-text">
                    Ask the customer what we can fix right now. If you need help, call ops.
                </div>
                <!-- #111: the ops managers on duty, as the HELP sheet lists them. One with a number
                     is a direct call; several are a list to pick from; none opens the HELP sheet,
                     which says who to ask instead. -->
                <ng-container *ngIf="callableOpsManagers$ | async as callable">
                    <a *ngIf="callable.length === 1" class="escalate-btn" [href]="'tel:' + callable[0].phone">
                        <i class="pi pi-phone"></i> Call Ops for Help
                    </a>
                    <ng-container *ngIf="callable.length > 1">
                        <button *ngIf="!pickingOps" type="button" class="escalate-btn" (click)="pickingOps = true">
                            <i class="pi pi-phone"></i> Call Ops for Help
                        </button>
                        <ng-container *ngIf="pickingOps">
                            <div class="call-list-title">Ops on duty today</div>
                            <a *ngFor="let m of callable" class="escalate-btn" [href]="'tel:' + m.phone">
                                <i class="pi pi-phone"></i> {{ m.name }}
                            </a>
                        </ng-container>
                    </ng-container>
                    <button *ngIf="callable.length === 0" type="button" class="escalate-btn" (click)="openHelp()">
                        <i class="pi pi-phone"></i> Call Ops for Help
                    </button>
                </ng-container>
            </div>

            <div *ngIf="rating >= threshold" style="color:#15803d;font-weight:700;font-size:1.05rem;margin-top:0.5rem;">
                <i class="pi pi-check-circle"></i> {{ threshold }} stars — great work!
            </div>

            <!-- #84/#111: the zone's review page (navigator.reviewUrl) as a QR for the customer to
                 scan. Hidden when the zone has not set one. -->
            <div class="review-qr" *ngIf="rating >= threshold && reviewQrDataUrl">
                <img [src]="reviewQrDataUrl" alt="QR code to leave a review">
                <div class="review-qr-caption">Ask the customer to scan this to leave a review</div>
            </div>
        </div>
    `,
})
export class NavStarRatingComponent extends NavBaseComponent implements OnInit, OnDestroy {
    rating = 0;
    feedback = '';
    /** Several ops managers are on duty and the crew asked to call: show the list (#111). */
    pickingOps = false;
    /** `navigator.reviewUrl` rendered as a QR image, or null when the zone has none (#84). */
    reviewQrDataUrl: string | null = null;

    /** On duty and reachable: the HELP sheet's list, minus anyone with no number on file. */
    readonly callableOpsManagers$ = this.store.select(createSelector(
        selectNavigatorOpsManagers,
        (managers) => (managers ?? []).filter((m): m is { name: string; phone: string } => !!m.phone),
    ));

    /** The script's `escalateThreshold`: ratings below it ask for a fix, at or above it for a review. */
    get threshold(): number {
        const t = Number(this.node?.props?.escalateThreshold);
        return Number.isFinite(t) && t > 0 ? t : 5;
    }

    get belowThreshold(): boolean { return this.rating > 0 && this.rating < this.threshold; }

    private subs = new Subscription();
    private eventId = '';

    /**
     * The crew's current step, held rather than read on demand — `Store.selectSignal` needs an
     * injection context and `persistRating()` runs from a click handler, where there isn't one.
     * Same reason `nav-collect-payment` holds it.
     */
    private currentStepId = '';

    constructor(
        private store: Store,
        private route: ActivatedRoute,
        private configGQL: GetConfigValuesGQL,
        private tagsGQL: TagsGQL,
        private addTagsGQL: AddTagsToObjectsGQL,
        // The step flow that owns the HELP sheet. Optional so the component still renders if a
        // script ever places it outside an `<EventNavigator>`; "Call Ops" then has nowhere to send
        // a crew with nobody to call, and does nothing.
        @Optional() @Inject(forwardRef(() => NavEventNavigatorComponent))
        private eventNavigator: NavEventNavigatorComponent | null,
    ) { super(); }

    ngOnInit(): void {
        this.eventId = this.route.snapshot.paramMap.get('eventId') ?? '';
        this.loadReviewQr();

        // Not async-pipeable: never rendered, only read inside the click handler below.
        // eslint-disable-next-line @ngrx/no-store-subscription
        this.subs.add(this.store.select(selectNavigatorCurrentStepId).subscribe((stepId) => {
            this.currentStepId = stepId ?? '';
        }));
    }

    /**
     * Leaving the step with a rating below the threshold tags the job `No Review` (#111), which
     * stops NiceJob asking an unhappy customer for a public review (`nicejobs.helpers.ts`).
     *
     * On leaving rather than on tapping, so the rating that counts is the final one: a crew that
     * taps 3 by mistake and corrects it to 5 never tags the job, and nothing has to be removed —
     * removing a tag is a different permission from adding one.
     */
    ngOnDestroy(): void {
        this.subs.unsubscribe();
        if (this.belowThreshold) { this.tagNoReview(); }
    }

    setRating(n: number) {
        this.rating = n;
        this.pickingOps = false;
        this.persistRating();
    }

    /** No ops manager with a number on file: the HELP sheet says who to ask instead. */
    openHelp(): void {
        if (this.eventNavigator) { this.eventNavigator.helpOpen = true; }
    }

    private loadReviewQr(): void {
        this.subs.add(this.configGQL.fetch({ keys: [ 'navigator.reviewUrl' ] }).subscribe({
            next: async (res) => {
                const url = (res?.data?.getConfigValues ?? [])
                    .find((c) => c?.key === 'navigator.reviewUrl')?.value?.trim();
                this.reviewQrDataUrl = url ? await QRCode.toDataURL(url, { width: 220, margin: 1 }) : null;
            },
            // A QR that cannot load is simply not shown; the rating step still works without it.
            error: (err) => console.warn('[NavStarRating] review URL could not be loaded', err?.message ?? err),
        }));
    }

    /**
     * Not awaited and not cancellable on purpose: it runs as the component is destroyed, and the
     * mutation must outlive it.
     */
    private tagNoReview(): void {
        const jobId = this.store.selectSignal(selectJobId)();
        if (!jobId) { return; }

        this.tagsGQL.fetch({ filter: { names: [ 'No Review' ], objectTypes: [ 'Job' ] } }).subscribe({
            next: (res) => {
                const tag = (res?.data?.tags?.tags ?? []).find((t) => t?.name === 'No Review');
                if (!tag) {
                    console.warn('[NavStarRating] no "No Review" tag in this zone; the job was not tagged');
                    return;
                }
                this.addTagsGQL.mutate({ tags: [ tag.id ], objects: [ jobId ], objectLabel: 'Job' }).subscribe({
                    error: (err) => console.warn('[NavStarRating] could not tag the job No Review', err?.message ?? err),
                });
            },
            error: (err) => console.warn('[NavStarRating] could not look up the No Review tag', err?.message ?? err),
        });
    }

    /** The feedback box is only shown below the threshold, so this fires on blur rather than per key. */
    onFeedbackBlur() { this.persistRating(); }

    /**
     * Writes the rating into `navigatorData` so the step's system comment can report it (**#63**).
     *
     * Before this the rating lived in a component field and died on step change — collected from
     * the customer and silently discarded, which is the same defect #43 fixed for photos.
     *
     * **This is only half the work, and the other half is in `COMMENTED_STEPS`.** A step that
     * writes a summary but is not registered there posts no comment at all, so the rating would
     * persist into `navigatorData` and still never surface to the office. Neither half is useful
     * alone. The step id comes from the slice rather than the component's `stepKey`, for the reason
     * spelled out in `nav-capture-storage.ts`: a component's key bears no relation to the step it
     * sits on.
     */
    private persistRating(): void {
        if (!this.eventId || !this.rating) { return; }

        this.store.dispatch(quickJsActions.navigatorDataPatched({
            eventId: this.eventId,
            patch: {
                // Two top-level scalars, NOT one nested object — and the key name is not free.
                //
                // `NAVIGATOR_NAMED_COLUMNS` (dynamicReports.helper.ts) already registers
                // `navigator_customerSatisfaction` as "Customer Rating (1-5)". The backend derives
                // that property name by flattening the top-level key, so only `customerSatisfaction`
                // lands in the column that has been waiting for it. The script agrees — the step
                // declares `stepKey="customer_satisfaction"`.
                //
                // And it has to be a **scalar**. `flattenEntry` writes scalars as scalars but
                // JSON-stringifies objects, so nesting `{ rating, feedback }` would produce
                // `navigator_starRating = '{"rating":4,…}'` — a string no report can aggregate.
                // That is exactly why `navigator_paymentMethod` is dead: collect-payment nests it.
                //
                // No `stepId` here, unlike the other step summaries. It would force an object, and
                // nothing reads it — the comment is keyed off the step being confirmed, not off this.
                customerSatisfaction: this.rating,
                customerSatisfactionFeedback: this.feedback?.trim() || null,
            },
        }));
    }
}

// ─── CrewRollCall — extracted to nav/nav-crew-roll-call.component.ts ──────────
// See that file for full implementation.
// Changes: crew-only role filter, 'unknown' default status, real updateClock mutation.

// ─── PhotoCapture — extracted to nav/nav-photo-capture.component.ts ───────────
// Full-screen camera overlay with getUserMedia, fallback to file input,
// compression, and damage annotation. Replaces the old inline file-picker version.


// ─── HelpButton (injected into top bar by EventNavigator) ─────────────────────

/** The pending `tawkChatMinimized` listener from the help sheet's support button, if any (#110). */
let onSupportChatMinimized: (() => void) | null = null;

@Component({
    selector: 'app-nav-help-sheet',
    standalone: true,
    imports: [CommonModule],
    styles: [`
        .overlay {
            position: fixed; inset: 0; background: rgba(0,0,0,0.5);
            z-index: 1000; display: flex; align-items: flex-end;
            animation: fadeIn 0.15s ease;
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .sheet {
            background: white; width: 100%; border-radius: 16px 16px 0 0;
            padding: 1.5rem 1.25rem 2rem;
            animation: slideUp 0.2s ease;
            max-height: 80vh; overflow-y: auto;
        }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .sheet-handle { width: 40px; height: 4px; background: #ddd; border-radius: 2px; margin: 0 auto 1.25rem; }
        .sheet-title { font-size: 1.2rem; font-weight: 700; color: #1a1a1a; margin-bottom: 1.25rem; text-align: center; }
        .help-btn {
            display: flex; align-items: center; gap: 1rem;
            width: 100%; padding: 1rem 1.1rem;
            background: white; border: 1.5px solid #e0e0e0;
            border-radius: 10px; margin-bottom: 0.75rem;
            cursor: pointer; text-align: left;
            font-size: 1rem; font-family: inherit;
        }
        .help-btn:active { background: #f5f5f5; }
        .help-btn i { font-size: 1.2rem; width: 1.5rem; text-align: center; }
        .help-btn .btn-label { font-weight: 600; color: #1a1a1a; display: block; }
        .help-btn .btn-sub   { font-size: 0.82rem; color: #888; display: block; margin-top: 0.1rem; }
        .confused-card {
            background: #f0f7ff; border: 1px solid #bfdbfe;
            border-radius: 10px; padding: 1rem 1.1rem; margin-bottom: 0.75rem;
        }
        .confused-title {
            font-size: 0.85rem; font-weight: 700; color: #1d4ed8;
            text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.5rem;
        }
        .confused-text { font-size: 0.95rem; color: #1e3a5f; line-height: 1.6; }
        .close-btn {
            display: block; width: 100%; padding: 0.85rem;
            background: #f0f0f0; border: none; border-radius: 10px;
            font-size: 1rem; font-weight: 600; color: #444;
            cursor: pointer; margin-top: 0.5rem;
        }
        .duty-card {
            background: #f6f9f6; border: 1px solid #cfe3cf;
            border-radius: 10px; padding: 0.9rem 1.1rem; margin-bottom: 0.75rem;
        }
        .duty-title {
            font-size: 0.85rem; font-weight: 700; color: #2d6a2d;
            text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.5rem;
        }
        .duty-row {
            display: flex; align-items: center; gap: 0.5rem;
            min-height: 44px; font-size: 0.98rem;
        }
        .duty-row + .duty-row { border-top: 1px solid #e4efe4; }
        .duty-name { font-weight: 600; color: #1a1a1a; }
        .duty-call {
            display: inline-flex; align-items: center; gap: 0.4rem;
            box-sizing: border-box; min-height: 44px; padding: 0.5rem 0;
            color: #2d6a2d; font-weight: 600; text-decoration: none;
        }
        .duty-nophone { color: #8a8a8a; font-size: 0.85rem; }
        .duty-empty { font-size: 0.92rem; color: #7a5c00; line-height: 1.5; }
        .duty-stale {
            display: flex; align-items: center; gap: 0.4rem;
            font-size: 0.85rem; color: #7a5c00; line-height: 1.4;
            margin-bottom: 0.4rem;
        }
    `],
    template: `
        <div class="overlay" (click)="close()" *ngIf="open">
            <div class="sheet" (click)="$event.stopPropagation()">
                <div class="sheet-handle"></div>
                <div class="sheet-title">How can we help?</div>

                <!-- I'm Confused — step-specific context -->
                <div class="confused-card" *ngIf="helpText">
                    <div class="confused-title">What do I do here?</div>
                    <div class="confused-text">{{ helpText }}</div>
                </div>

                <!-- On duty today (#92). Replaced the Call/Text Ops buttons, which never rendered:
                     they read job.metadata.opsPhone and nothing has ever written it (removed, #45). -->
                <div class="duty-card">
                    <div class="duty-title">On duty today</div>

                    <!--
                        A failed refresh keeps the list and says so, rather than emptying it. The
                        empty state below is an assertion — "nobody is on duty" — and making it
                        after a dropped request would be a confident falsehood that also costs the
                        crew a number that is almost certainly still good.
                    -->
                    <div class="duty-stale" *ngIf="opsManagersStale && opsManagers?.length">
                        <i class="pi pi-exclamation-triangle"></i>
                        Couldn't refresh — this may be out of date
                    </div>

                    <div class="duty-row" *ngFor="let m of opsManagers">
                        <a *ngIf="m.phone" class="duty-call" [href]="'tel:' + m.phone">
                            <i class="pi pi-phone"></i>{{ m.name }}
                        </a>
                        <ng-container *ngIf="!m.phone">
                            <span class="duty-name">{{ m.name }}</span>
                            <span class="duty-nophone">· no number on file</span>
                        </ng-container>
                    </div>

                    <!-- Nothing held AND the lookup failed: say that, not "nobody is assigned". -->
                    <div class="duty-empty" *ngIf="!opsManagers?.length && opsManagersStale">
                        Couldn't load who is on duty. Check your signal and reopen this sheet, or
                        call the office.
                    </div>

                    <div class="duty-empty" *ngIf="!opsManagers?.length && !opsManagersStale">
                        No ops manager assigned for today. Ask dispatch to assign one on the
                        Dispatch board.
                    </div>
                </div>

                <!-- Move Right Support -->
                <button class="help-btn" (click)="openSupport()">
                    <i class="pi pi-headphones" style="color:#7c3aed;"></i>
                    <span>
                        <span class="btn-label">Move Right Support</span>
                        <span class="btn-sub">Chat with the Move Right team</span>
                    </span>
                </button>
                <div class="duty-empty support-closed" *ngIf="supportChatClosed">
                    Support chat is closed right now. For help now, call the ops manager on duty above.
                </div>

                <button class="close-btn" (click)="close()">Close</button>
            </div>
        </div>
    `,
})
export class NavHelpSheetComponent {
    @Input() open = false;
    @Input() helpText = '';
    @Input() opsManagers: { name: string; phone: string | null }[] = [];
    @Input() opsManagersStale = false;

    @Output() closeSheet = new EventEmitter<void>();

    /** The crew asked for the support chat and Tawk is not taking chats (#110). */
    supportChatClosed = false;

    /**
     * Opens the app's Tawk chat over the navigator, so the crew keeps their place mid-step (#110).
     *
     * The navigator hides Tawk twice — `hideWidget()` on entry, and `app.main.scss` hiding every Tawk
     * element while `body.navigator-open` is set — and `maximize()` alone shows nothing through
     * either. So the chat lifts both while it is open: `navigator-chat-open` exempts Tawk from the
     * CSS, `showWidget()` undoes the other. Minimising hides it again, but only while the navigator
     * is still open; after it has closed, the bubble belongs to the rest of the app.
     *
     * Out of hours, or with no signal, Tawk is not `online` and the account does not show the
     * widget at all, so opening it would do nothing. The sheet says so and points to the ops manager
     * on duty, listed just above.
     */
    openSupport() {
        const tawk = (window as any).Tawk_API;
        let online = false;
        try { online = tawk?.getStatus?.() === 'online'; } catch { /* not loaded: treat as closed */ }

        if (!online) {
            this.supportChatClosed = true;
            return;
        }

        this.supportChatClosed = false;
        document.body.classList.add('navigator-chat-open');
        // The window event, not `Tawk_API.onChatMinimized`: Tawk reads its `on*` callbacks when it
        // loads, so one assigned now never fires (checked 2026-10-05). The event does.
        // One listener at a time: a chat closed without minimising never fires it, and the next tap
        // would otherwise add another. Module-level, as the listener outlives this sheet.
        if (onSupportChatMinimized) { window.removeEventListener('tawkChatMinimized', onSupportChatMinimized); }
        onSupportChatMinimized = () => {
            onSupportChatMinimized = null;
            document.body.classList.remove('navigator-chat-open');
            if (document.body.classList.contains('navigator-open')) { tawk.hideWidget?.(); }
        };
        window.addEventListener('tawkChatMinimized', onSupportChatMinimized, { once: true });
        tawk.showWidget?.();
        tawk.maximize?.();
        this.closeSheet.emit();
    }

    /** The "closed" note is about that moment; the next opening of the sheet starts without it. */
    close() {
        this.supportChatClosed = false;
        this.closeSheet.emit();
    }
}

// ─── MovePerformanceSummary ────────────────────────────────────────────────────

@Component({
    selector: 'app-nav-move-performance-summary',
    standalone: true,
    imports: [CommonModule],
    styles: [`
        :host { display: block; margin-bottom: 1rem; }
        .summary-card {
            background: white; border-radius: 10px;
            box-shadow: 0 1px 4px rgba(0,0,0,0.07);
            overflow: hidden;
        }
        .summary-header {
            background: #1a1a1a; color: white;
            padding: 0.85rem 1.1rem;
            font-size: 0.85rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em;
        }
        .metric-row {
            display: flex; align-items: center; justify-content: space-between;
            padding: 0.85rem 1.1rem; border-bottom: 1px solid #f0f0f0;
        }
        .metric-row:last-child { border-bottom: none; }
        .metric-label { font-size: 0.95rem; color: #555; }
        .metric-vals { text-align: right; }
        .metric-actual { font-size: 1rem; font-weight: 700; color: #1a1a1a; }
        .metric-est    { font-size: 0.8rem; color: #aaa; margin-top: 0.1rem; }
        .delta-pill {
            display: inline-block; padding: 0.2rem 0.6rem;
            border-radius: 20px; font-size: 0.78rem; font-weight: 700; margin-left: 0.4rem;
        }
        .delta-good { background: #dcfce7; color: #15803d; }
        .delta-bad  { background: #fee2e2; color: #b91c1c; }
        .lbs-row {
            background: #f0f7f0; padding: 0.85rem 1.1rem;
            display: flex; align-items: center; justify-content: space-between;
        }
        .lbs-label { font-size: 0.85rem; color: #2d6a2d; font-weight: 600; }
        .lbs-value { font-size: 1.1rem; font-weight: 800; color: #2d6a2d; }
    `],
    template: `
        <div class="summary-card">
            <div class="summary-header">Move Performance Summary</div>

            <div class="metric-row">
                <span class="metric-label">Load time</span>
                <div class="metric-vals">
                    <div class="metric-actual">
                        {{ fmt(actualLoad) }}
                        <span class="delta-pill"
                              [class.delta-good]="actualLoad <= estimatedLoad"
                              [class.delta-bad]="actualLoad > estimatedLoad">
                            {{ actualLoad <= estimatedLoad ? '✓' : '+' }}{{ fmt(Math.abs(actualLoad - estimatedLoad)) }}
                        </span>
                    </div>
                    <div class="metric-est">Estimated {{ fmt(estimatedLoad) }}</div>
                </div>
            </div>

            <div class="metric-row">
                <span class="metric-label">Unload time</span>
                <div class="metric-vals">
                    <div class="metric-actual">
                        {{ fmt(actualUnload) }}
                        <span class="delta-pill"
                              [class.delta-good]="actualUnload <= estimatedUnload"
                              [class.delta-bad]="actualUnload > estimatedUnload">
                            {{ actualUnload <= estimatedUnload ? '✓' : '+' }}{{ fmt(Math.abs(actualUnload - estimatedUnload)) }}
                        </span>
                    </div>
                    <div class="metric-est">Estimated {{ fmt(estimatedUnload) }}</div>
                </div>
            </div>

            <div class="metric-row">
                <span class="metric-label">Total move time</span>
                <div class="metric-vals">
                    <div class="metric-actual">{{ fmt(actualTotal) }}</div>
                    <div class="metric-est">Estimated {{ fmt(estimatedTotal) }}</div>
                </div>
            </div>

            <div class="lbs-row" *ngIf="lbsPerManHr > 0">
                <span class="lbs-label">Overall rate</span>
                <span class="lbs-value">{{ lbsPerManHr | number:'1.0-0' }} lbs/man-hr</span>
            </div>
        </div>
    `,
})
export class NavMovePerformanceSummaryComponent extends NavBaseComponent implements OnInit {
    Math = Math;
    private _navData: Record<string, any> = {};

    constructor(private store: Store) { super(); }

    ngOnInit() {
        // Read navigatorData from the store to get real step timings.
        //
        // Hand-rolled router walk plus a string-keyed reach into the Jobs slice, read once
        // via subscribe-then-unsubscribe. `selectJob` (job-tool.reducer) already composes
        // exactly this and would replace the whole block; the rewrite is deliberately not
        // bundled into the #14 fix. Suppressed rather than silently left failing — see #8.
        // eslint-disable-next-line @ngrx/prefer-selector-in-select
        this.store.select((s: any) => {
            const walk = (n: any): string | null => n?.params?.eventId || walk(n?.firstChild);
            const walk2 = (n: any): string | null => n?.params?.jobId || walk2(n?.firstChild);
            try {
                const eventId = walk(s?.router?.state?.root);
                const jobId = walk2(s?.router?.state?.root);
                const raw = s?.['Jobs']?.jobs?.[jobId]?.events?.find((e: any) => e.id === eventId)?.navigatorData;
                return raw ? JSON.parse(raw) : {};
            } catch { return {}; }
        // eslint-disable-next-line @ngrx/no-store-subscription
        }).subscribe(data => { this._navData = data ?? {}; }).unsubscribe();
    }

    /** Duration in seconds between two step IDs, reading from navigatorData.steps */
    private _stepDuration(stepId: string): number {
        const step = this._navData?.steps?.[stepId];
        if (!step?.startedAt || !step?.completedAt) return 0;
        return Math.max(0, Math.round((step.completedAt - step.startedAt) / 1000));
    }

    get estimatedLoad(): number  { return this.node.props?.estimatedLoadSeconds ?? 0; }
    get estimatedUnload(): number { return this.node.props?.estimatedUnloadSeconds ?? 0; }

    /** Actual load time from navigator step data; falls back to 0 until completed */
    get actualLoad(): number {
        const fromNav = this._stepDuration('load');
        return fromNav > 0 ? fromNav : (this.node.props?.actualLoadSeconds ?? 0);
    }

    /** Actual unload time from navigator step data */
    get actualUnload(): number {
        const fromNav = this._stepDuration('unload');
        return fromNav > 0 ? fromNav : (this.node.props?.actualUnloadSeconds ?? 0);
    }

    get estimatedTotal(): number { return this.estimatedLoad + this.estimatedUnload; }
    get actualTotal(): number    { return this.actualLoad + this.actualUnload; }

    get lbsPerManHr(): number {
        const w = this.node.props?.totalWeight ?? 0;
        const crew = this.node.props?.crewSize ?? 1;
        const hrs = this.actualTotal / 3600;
        if (!w || !hrs) return 0;
        return Math.round(w / (hrs * crew));
    }

    fmt(seconds: number): string {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        if (h > 0) return `${h}h ${m}m`;
        return `${m}m`;
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// STEP SHELL — renders children by type
// ─────────────────────────────────────────────────────────────────────────────

@Component({
    selector: 'app-nav-step',
    standalone: true,
    imports: [
        CommonModule,
        NavCustomerInfoComponent,
        NavLocationInfoComponent,
        NavCheckboxComponent,
        NavInventoryEditorFromNav,
        NavInventorySummaryComponent,
        NavCustomerSignatureComponent,
        NavJobNotesComponent,
        NavHomeProtectionComponent,
        NavStarRatingComponent,
        NavMovePerformanceSummaryComponent,
        NavCrewRollCallComponent,
        NavPhotoCaptureFromNav,
        NavPandaDocSignerComponent,
        NavLocationPermissionComponent,
        NavLoadProgressComponent,
        NavCollectPaymentComponent,
    ],
    styles: [`
        .nav-button {
            display: flex; align-items: center; justify-content: center; gap: 0.5rem;
            width: 100%; padding: 1rem 1rem; margin-bottom: 0.75rem;
            background: white; border: 1.5px solid #ced4da; border-radius: 8px;
            font-size: 1rem; cursor: pointer; font-family: inherit;
        }
        .nav-button:active { background: #f0f0f0; }
        .nav-button i { color: #555; }
        /* href buttons render as <a>; keep them indistinguishable from <button> */
        a.nav-button { text-decoration: none; color: inherit; box-sizing: border-box; }
        .notes-section { margin-top: 1rem; }
        .notes-label {
            font-size: 0.8rem; color: #888; font-weight: 600;
            text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.35rem;
        }
        .notes-box {
            width: 100%; box-sizing: border-box; padding: 0.85rem;
            border: 1.5px solid #ced4da; border-radius: 8px;
            font-size: 1rem; resize: none; font-family: inherit;
        }
        .notes-box:focus { outline: none; border-color: #2d6a2d; }
        .step-note { font-size: 0.9rem; color: #666; white-space: pre-line; margin: 0.35rem 0 0.75rem; line-height: 1.55; }
        .step-note.centered { text-align: center; }
        .step-note.gray { color: #999; }
        .step-note.small { font-size: 0.8rem; }
    `],
    template: `
        <div class="step-content">
            <ng-container *ngFor="let child of node.children">

                <app-nav-customer-info
                    *ngIf="child.type === 'CustomerInfo'"
                    [node]="child">
                </app-nav-customer-info>

                <app-nav-location-info
                    *ngIf="child.type === 'LocationInfo'"
                    [node]="child">
                </app-nav-location-info>

                <app-nav-checkbox
                    *ngIf="child.type === 'checkbox' || child.type === 'Checkbox'"
                    [node]="child">
                </app-nav-checkbox>

                <app-nav-inventory-editor
                    *ngIf="child.type === 'inventory_editor' || child.type === 'InventoryEditor'"
                    [node]="child">
                </app-nav-inventory-editor>

                <app-nav-inventory-summary
                    *ngIf="child.type === 'inventory_summary' || child.type === 'InventorySummary'"
                    [node]="child">
                </app-nav-inventory-summary>

                <app-nav-customer-signature
                    *ngIf="child.type === 'customer_signature' || child.type === 'CustomerSignature'"
                    [node]="child">
                </app-nav-customer-signature>

                <app-nav-job-notes
                    *ngIf="child.type === 'CrewNotes' || child.type === 'JobNotes'"
                    [node]="child">
                </app-nav-job-notes>

                <app-nav-home-protection
                    *ngIf="child.type === 'HomeProtection'"
                    [node]="child">
                </app-nav-home-protection>

                <app-nav-star-rating
                    *ngIf="child.type === 'StarRating'"
                    [node]="child">
                </app-nav-star-rating>

                <app-nav-move-performance-summary
                    *ngIf="child.type === 'MovePerformanceSummary'"
                    [node]="child">
                </app-nav-move-performance-summary>

                <app-nav-crew-roll-call
                    *ngIf="child.type === 'CrewRollCall'"
                    [node]="child">
                </app-nav-crew-roll-call>

                <app-nav-photo-capture
                    *ngIf="child.type === 'PhotoCapture'"
                    [node]="child">
                </app-nav-photo-capture>

                <app-nav-pandadoc-signer
                    *ngIf="child.type === 'PandaDocSigner'"
                    [node]="child">
                </app-nav-pandadoc-signer>

                <app-nav-location-permission
                    *ngIf="child.type === 'LocationPermission'"
                    [node]="child">
                </app-nav-location-permission>

                <app-nav-load-progress
                    *ngIf="child.type === 'LoadProgress'"
                    [node]="child">
                </app-nav-load-progress>

                <app-nav-collect-payment
                    *ngIf="child.type === 'CollectPayment'"
                    [node]="child">
                </app-nav-collect-payment>

                <!-- Generic button.
                     Renders as an anchor when the script supplies an href. This is the only way
                     a script-declared button can do anything: props cross the QuickJS boundary
                     through ctx.dump(), which drops functions but preserves strings, so a URL
                     is expressible where a click handler is not (see #24). -->
                <a *ngIf="(child.type === 'button' || child.type === 'Button') && child.props?.href"
                   class="nav-button"
                   [href]="buildHref(child)">
                    <i *ngIf="child.props?.icon" [class]="child.props.icon"></i>
                    {{ child.props?.label }}
                </a>

                <button *ngIf="(child.type === 'button' || child.type === 'Button') && !child.props?.href"
                        class="nav-button"
                        (click)="child.props?.click?.()">
                    <i *ngIf="child.props?.icon" [class]="child.props.icon"></i>
                    {{ child.props?.label }}
                </button>

                <!-- Generic paragraph -->
                <p *ngIf="child.type === 'p' || child.type === 'P'"
                   class="step-note"
                   [class.centered]="child.props?.className?.includes('centered')"
                   [class.gray]="child.props?.className?.includes('gray')"
                   [class.small]="child.props?.className?.includes('small')">
                    {{ child.props?.text }}
                </p>

            </ng-container>

            <!-- Optional additional notes textarea -->
            <div class="notes-section" *ngIf="node.props?.showNotes">
                <div class="notes-label">Additional notes</div>
                <textarea class="notes-box" rows="4" placeholder="Add notes here..."></textarea>
            </div>
        </div>
    `,
})
export class NavStepComponent extends NavBaseComponent {

    /**
     * Final `href` for a script-declared button, applying the one piece of platform knowledge
     * the script cannot have.
     *
     * A `body` prop is only meaningful for `sms:`, and the separator before it is
     * platform-split: iOS wants `&body=`, everything else `?body=`. The script runs inside
     * QuickJS with no `navigator`, so this has to happen here. Anything else — `tel:`, plain
     * links — passes through untouched.
     */
    buildHref(child: NavNode): string {
        const href: string = child.props?.href ?? '';
        const body: string = child.props?.body ?? '';

        if (!body || !href.startsWith('sms:')) { return href; }

        // iPadOS 13+ reports itself as a Mac, so the touch check is not redundant.
        const ua = navigator.userAgent;
        const isApple = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);

        return `${href}${isApple ? '&' : '?'}body=${encodeURIComponent(body)}`;
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// EVENT NAVIGATOR — manages step progression, top bar, timer, undo, help
// ─────────────────────────────────────────────────────────────────────────────

const UNDO_DURATION_S = 5;

/**
 * Node types that `_registerStepGates` gates *unconditionally*, in both spellings the renderer
 * accepts. Used only to warn when one of them ends up without a reachable `onReady`, which
 * strands the crew on a permanently disabled confirm button.
 *
 * Deliberately excludes the conditionally-gated ones — `PhotoCapture` without `required`,
 * `CustomerSignature`/`PandaDocSigner` marked optional — where having no `onReady` is correct.
 */
const ALWAYS_GATED_NODE_TYPES = new Set([
    'InventoryEditor', 'inventory_editor',
    'CrewRollCall',
    'HomeProtection',
    'LoadProgress',
    'Checkbox', 'checkbox',
]);

/**
 * Whether a step component cannot do its job without the server (#86a): the contract, a customer
 * signature, a photo upload and the payment all talk to it as they happen, so with no signal they
 * cannot be queued like the crew's progress. Only the ones the crew cannot skip — the same
 * conditions `_registerStepGates` gates on, plus the payment — block Continue; an optional one just
 * fails on its own and the crew can move past it.
 */
function needsServer(child: NavNode): boolean {
    const p = child.props ?? {};
    switch (child.type) {
        case 'PandaDocSigner': return !p.optional;
        case 'CustomerSignature':
        case 'customer_signature': return p.enabled !== false && !p.optional;
        case 'PhotoCapture': return Boolean(p.required);
        case 'CollectPayment': return true;
        default: return false;
    }
}

@Component({
    selector: 'app-nav-event-navigator',
    standalone: true,
    imports: [CommonModule, NavStepComponent, NavHelpSheetComponent, NavStepTimerToggleComponent, NavStepTimerDetailComponent],
    styles: [`
        /*
         * Full-height column layout.
         * On phones: takes full viewport width.
         * On tablet/desktop: the parent page shell centres this at max 520px.
         */
        :host {
            display: flex; flex-direction: column; height: 100%; width: 100%;
            background: #f5f7fa;
        }

        /* ── Top bar ── */
        .nav-topbar {
            background: white;
            border-bottom: 1px solid #e0e0e0;
            /* Vertical padding trimmed when the controls went to 44px (#100b), so the bar grows
               by a few px rather than by the full difference. */
            padding: 0.3rem 1rem;
            display: flex; align-items: center; gap: 0.4rem;
            flex-shrink: 0;
            min-height: 48px;
        }
        .topbar-info {
            flex: 1; min-width: 0;
            font-size: 0.8rem; color: #888; font-weight: 500;
        }
        .topbar-step { font-weight: 700; color: #1a1a1a; }
        .topbar-customer { color: #555; }
        /* Truncate long customer name + step title gracefully */
        .topbar-title {
            display: block; font-size: 0.75rem; color: #888;
            white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        /* Header controls at 44px (#100b, trial — the developer may revert after seeing it). */
        .topbar-actions { display: flex; align-items: center; gap: 0.3rem; flex-shrink: 0; }
        .icon-btn {
            background: none; border: none; cursor: pointer;
            font-size: 1.1rem; color: #555; min-height: 44px; padding: 0 0.55rem;
            border-radius: 8px; display: flex; align-items: center; gap: 0.3rem;
        }
        .icon-btn:hover { background: #f0f0f0; }
        .icon-btn .help-label { font-size: 0.85rem; font-weight: 600; }
        .exit-btn {
            background: none; border: 1px solid #e0e0e0; cursor: pointer;
            font-size: 0.9rem; color: #666; min-height: 44px; padding: 0 0.9rem;
            border-radius: 8px; font-family: inherit; font-weight: 500;
        }
        .exit-btn:hover { background: #f5f5f5; }

        /* ── Progress bar ── */
        .progress-track {
            height: 3px; background: #e8e8e8; flex-shrink: 0;
        }
        .progress-fill {
            height: 100%; background: #2d6a2d;
            transition: width 0.4s ease;
        }

        /* ── Scrollable step body ── */
        .step-title {
            font-size: 1.3rem; font-weight: 700; text-align: center;
            color: #1a1a1a; padding: 1.1rem 1.25rem 0.5rem;
        }
        .step-body {
            flex: 1; overflow-y: auto; padding: 0.25rem 1.1rem 0.75rem;
            -webkit-overflow-scrolling: touch;
        }

        /* ── Sticky footer ── */
        .step-footer {
            flex-shrink: 0; background: white;
            border-top: 1px solid #e0e0e0;
            padding: 0.75rem 1rem;
            box-sizing: border-box;
        }

        /*
         * Undo button — shown for 5s after advancing.
         * A shrinking progress bar gives a visual countdown.
         */
        .undo-btn {
            display: flex; align-items: center; gap: 0.75rem;
            width: 100%; padding: 0.9rem 1rem;
            background: #2d2d2d; color: white;
            border: none; border-radius: 10px;
            font-size: 1rem; font-weight: 500; cursor: pointer;
            position: relative; overflow: hidden;
            margin-bottom: 0.6rem; text-align: left; font-family: inherit;
        }
        .undo-btn:hover { background: #1a1a1a; }
        .undo-btn-left { flex: 1; }
        .undo-btn-label { font-size: 1rem; font-weight: 600; }
        .undo-btn-sub { font-size: 0.78rem; opacity: 0.65; margin-top: 0.1rem; }
        .undo-btn-timer { font-size: 1.4rem; font-weight: 800; flex-shrink: 0; }
        .undo-progress {
            position: absolute; bottom: 0; left: 0; height: 3px;
            background: rgba(255,255,255,0.4); transition: width 1s linear;
        }

        /* ── Confirm button ── */
        .confirm-btn {
            display: flex; align-items: center; justify-content: center; gap: 0.6rem;
            width: 100%; padding: 1.1rem;
            background: #2d6a2d; color: white;
            border: none; border-radius: 10px;
            font-size: 1.1rem; font-weight: 700; cursor: pointer;
            transition: background 0.15s; box-sizing: border-box;
            letter-spacing: 0.01em; font-family: inherit;
        }
        .confirm-btn:active  { background: #1f4d1f; }
        .confirm-btn:disabled { background: #b0b8b0; cursor: not-allowed; }
        .confirm-btn.finish  { background: #1a5276; }
        .confirm-btn.finish:active { background: #154360; }

        /* A step that needs the server, with no signal (#86a). */
        .needs-signal {
            display: flex; align-items: center; gap: 0.5rem;
            margin-bottom: 0.6rem; padding: 0.7rem 0.9rem;
            background: #fef2f2; color: #991b1b;
            border: 1px solid #fecaca; border-radius: 10px;
            font-size: 0.95rem; font-weight: 600;
        }
    `],
    template: `
        <ng-container *ngIf="activeStep as step">

            <!-- Top bar: step counter | customer name | step title | HELP | Exit -->
            <div class="nav-topbar">
                <div class="topbar-info">
                    <span class="topbar-step">{{ activeIndex + 1 }}/{{ steps.length }}</span>
                    <span class="topbar-customer" *ngIf="customerName"> · {{ customerName }}</span>
                    <span class="topbar-title">{{ step.props?.title }}</span>
                </div>
                <div class="topbar-actions">
                    <!-- Step clock, left of HELP. Expands the detail block below the progress bar. -->
                    <app-nav-step-timer-toggle
                        *ngIf="timerVisible"
                        [elapsedSec]="stepElapsedSec"
                        [budgetSec]="stepBudgetSec"
                        [expanded]="timerExpanded"
                        (toggled)="timerExpanded = !timerExpanded">
                    </app-nav-step-timer-toggle>

                    <!-- HELP button — always visible -->
                    <button class="icon-btn" (click)="helpOpen = true" title="Help">
                        <i class="pi pi-question-circle"></i>
                        <span class="help-label">HELP</span>
                    </button>
                    <button class="exit-btn" (click)="exit()">Exit</button>
                </div>
            </div>

            <!-- Overall progress bar — step count, not time. The timer below covers time. -->
            <div class="progress-track">
                <div class="progress-fill" [style.width.%]="progressPct"></div>
            </div>

            <!-- Step / whole-job budgets, revealed by the clock in the top bar. In flow, so
                 expanding pushes the step content down rather than covering it. -->
            <app-nav-step-timer-detail
                *ngIf="timerVisible && timerExpanded"
                [elapsedSec]="stepElapsedSec"
                [budgetSec]="stepBudgetSec"
                [totalElapsedSec]="totalElapsedSec"
                [totalBudgetSec]="totalBudgetSec">
            </app-nav-step-timer-detail>

            <!-- Step title + scrollable body -->
            <div class="step-title">{{ step.props?.title }}</div>
            <div class="step-body">
                <app-nav-step [node]="step"></app-nav-step>
            </div>

            <!-- Sticky footer: undo → confirm -->
            <div class="step-footer">
                <button class="undo-btn" *ngIf="undoVisible" (click)="undo()">
                    <div class="undo-btn-left">
                        <div class="undo-btn-label">← Back to Step {{ activeIndex }}</div>
                        <div class="undo-btn-sub">Tap to undo</div>
                    </div>
                    <div class="undo-btn-timer">{{ undoSecondsLeft }}s</div>
                    <div class="undo-progress" [style.width.%]="undoProgress"></div>
                </button>

                <div class="needs-signal" *ngIf="needsSignal" role="status">
                    <i class="pi pi-wifi"></i>Needs signal — continue when connected.
                </div>

                <button class="confirm-btn"
                        [class.finish]="isLast"
                        [disabled]="step.props?.confirmButtonEnabled === false || undoVisible || !canProceed || needsSignal"
                        (click)="confirm(step)">
                    <i [class]="isLast ? 'pi pi-flag-fill' : 'pi pi-check'"></i>
                    {{ step.props?.confirmButton || (isLast ? 'Finish Job' : 'Continue') }}
                </button>
            </div>

        </ng-container>

        <!-- Help bottom sheet — rendered outside the step flow so it overlays everything -->
        <app-nav-help-sheet
            [open]="helpOpen"
            [helpText]="activeHelpText"
            [opsManagers]="(opsManagers$ | async) ?? []"
            [opsManagersStale]="(opsManagersStale$ | async) ?? false"
            (closeSheet)="helpOpen = false">
        </app-nav-help-sheet>
    `,
})
export class NavEventNavigatorComponent extends NavBaseComponent implements OnInit, OnDestroy {

    activeIndex = 0;
    helpOpen = false;
    canProceed = true;
    /** The phone reports a connection. Not proof the server can be reached — see `needsSignal`. */
    isOnline = true;

    undoVisible = false;
    undoSecondsLeft = UNDO_DURATION_S;
    undoProgress = 100;

    private _timerInterval: any;
    private _undoInterval: any;
    private _stepStartTime = Date.now();
    private _storeSub: Subscription | null = null;
    private _gateSub: Subscription | null = null;
    private _startTimeSub: Subscription | null = null;
    private _onlineSub: Subscription | null = null;

    /** Seconds on the current step. Recomputed once a second by `_runTimer`. */
    stepElapsedSec = 0;
    /** Seconds across the whole run: completed steps plus the current one. */
    totalElapsedSec = 0;

    /**
     * Whether the timer's detail rows are showing.
     *
     * Survives every step change for free: this component is created once per run
     * (`job-navigator-page.component.ts` assigns `navigatorTree` once and renders it once), so a
     * plain field is enough. Deliberately not persisted across a reload — the navigator touches
     * no `localStorage`, and the app's only persistence is a five-key typed allowlist that this
     * slice is not part of. Not worth being the first consumer of a new storage pattern for a
     * chevron.
     */
    timerExpanded = false;

    /**
     * Wall-clock start of the whole run: the earliest `startedAt` in the persisted step map.
     * Seeded once at mount, so a reload mid-run does not reset the whole-job total.
     *
     * Whole-job elapsed is `now - _runStartedAt`, **not** the sum of each step's duration. Summing
     * was the first implementation and it was wrong twice over:
     *
     *   - spans can **overlap**. On event `f8a8ebb4`, `drive_to_start` completed at 10:06:52 while
     *     its successor `arrival_start` had already started at 10:04:40 — 132s counted twice.
     *     Overlap is a symptom of other defects (an ops timing edit, a stale-snapshot clobber),
     *     but the timer should not compound them into a wrong number;
     *   - it needed running bookkeeping in `confirm()` and `undo()` that had to cancel exactly,
     *     which is state to get wrong for no benefit.
     *
     * Wall clock counts idle time — a navigator left open counts the gap. That is deliberate: for
     * "how long has this job taken", the answer a crew wants is the clock on the wall, and a job
     * genuinely paused for an hour genuinely took that hour.
     *
     * **Deliberately not a live subscription to `selectJob`.** Re-deriving anything from that
     * shared object on every job change is what #46 turned out to be, and a one-shot seed is all
     * this needs.
     */
    private _runStartedAt: number | null = null;

    /**
     * The `navigatorCurrentStep` the store held before the current optimistic advance, kept so
     * `undo()` and a failed mutation can put it back. `null` when no advance is pending. See #31.
     */
    private _stepBeforeOptimisticAdvance: string | null = null;

    /**
     * `_stepStartTime` as it stood before the current advance, so `undo()` can restore the
     * returned step's original start rather than re-stamping it. `null` when nothing is pending.
     * See `navigatorStepUndone` for why re-stamping is wrong.
     */
    private _stepStartTimeBeforeAdvance: number | null = null;

    /** Who is on duty today, for the HELP sheet. Loaded by the navigator's own effect. */
    readonly opsManagers$: Observable<{ name: string; phone: string | null }[]>;
    /** The last refresh of the duty list failed, so the sheet marks what it shows as possibly stale. */
    readonly opsManagersStale$: Observable<boolean>;

    constructor(
        private store: Store,
        private router: Router,
        private route: ActivatedRoute,
        private gate: NavStepGateService,
        private onlineStatus: OnlineStatusService,
    ) {
        super();
        this.opsManagers$ = this.store.select(selectNavigatorOpsManagers);
        this.opsManagersStale$ = this.store.select(selectNavigatorOpsManagersStale);
    }

    ngOnInit() {
        this._storeSub = this.store.select(selectNavigatorCurrentStepId).pipe(
            filter(stepId => !!stepId),
            distinctUntilChanged(),
        ).subscribe(stepId => {
            const idx = this.steps.findIndex(s => s.props?.id === stepId);

            if (idx < 0) {
                // The persisted step is not in this script. Causes seen so far: a component
                // writing a synthetic id over `currentStep` (#16), and a phantom left by an
                // older script (#2). A renamed step or a script swapped under a running job
                // would do it too.
                //
                // This branch exists because the alternative is worse than being on the wrong
                // step. Previously the whole block was guarded by `if (idx >= 0)` with no
                // `else`, so an unknown id skipped `gate.reset()` and `_registerStepGates()`
                // as well — leaving the confirm button permanently disabled with no way
                // forward and no way back. Landing on the wrong step is recoverable by the
                // crew; a dead button is not.
                console.warn(
                    `[Navigator] persisted step "${stepId}" is not one of this script's `
                    + `${this.steps.length} steps — staying on step ${this.activeIndex + 1} `
                    + `("${this.steps[this.activeIndex]?.props?.id}"). The run is usable; the `
                    + `stored step id is stale or was written by something that is not a step.`,
                );
                this.gate.reset();
                this._registerStepGates(this.steps[this.activeIndex]);
                this._runTimer();
                return;
            }

            // Already standing on this step with its gates live? Then this emission is not a
            // navigation — it is our own `confirm()` coming back around after the undo window,
            // which dispatches `navigatorStepConfirmed` five seconds *after* it moved us here.
            // Re-running the setup below would re-register the step's gates as unsatisfied and
            // silently discard anything the crew answered during those five seconds; the
            // components do not re-mount, so nothing would re-report it and the step would
            // re-block (#29).
            //
            // The `_gatesRegisteredForIndex` check is what makes this safe: a plain
            // `idx === this.activeIndex` guard would also skip a *genuine* restore that happens
            // to land on the index the component already starts at, leaving that step's gates
            // unregistered.
            if (idx === this.activeIndex && this._gatesRegisteredForIndex === idx) {
                this._runTimer();
                return;
            }

            this.activeIndex = idx;
            this._runTimer();
            // Reset gate for new step
            this.gate.reset();
            this._registerStepGates(this.steps[idx]);
        });

        // Track the step's start time for as long as this component lives — NOT a one-shot read.
        //
        // It used to be one-shot (`.subscribe(...).unsubscribe()`), on the reasoning that a step's
        // start is captured once when the step mounts. That is wrong for every value that arrives
        // *after* mount, and two do:
        //
        //   - the resume path's second, server-sourced correction, which lands whenever the job
        //     fetch completes after the navigator has already rendered;
        //   - `navigatorStepOverridden` — the ops override. The step id subscription below fired and
        //     moved the crew, but `_stepStartTime` kept the *previous* step's start, so the timer
        //     showed that step's elapsed on the new one. Ops moving a crew is a required flow.
        //
        // Safe to subscribe now in a way it would not have been before #46: `navigatorStepStartTime`
        // is written only by explicit actions (confirm, undo, restore, override) and is never
        // re-derived from `selectJob`, so this cannot reintroduce the shared-state dependency the
        // rework removed.
        // eslint-disable-next-line @ngrx/no-store-subscription
        this._startTimeSub = this.store.select(selectNavigatorStepStartTime)
            .pipe(filter((startTime): startTime is number => !!startTime), distinctUntilChanged())
            .subscribe(startTime => {
                this._stepStartTime = startTime;
                this._runTimer();
            });

        // Register the opening step's gates on a fresh start.
        //
        // The subscription above is the only thing that registers gates on arrival, and it is
        // guarded by `filter(stepId => !!stepId)` — so a run with nothing to restore never
        // registers anything, and the first step's gated components silently gate nothing:
        // `props.onReady` is never assigned, so their `_notifyGate()` calls into undefined, the
        // gate holds no entry to block on, and `canProceed` is true from the first frame.
        //
        // Latent rather than observed — the current script's `confirm_job` has no gated child —
        // but #27 made `Checkbox` a gating type, so one checkbox added to step 1 would now fail
        // open. Failing open is the dangerous direction: #25's other half at least blocked.
        //
        // Guarded on the marker rather than on `activeIndex`, so a restore that already ran
        // synchronously when we subscribed is not re-registered and its answers survive.
        //
        // `reset()` first, as every other registration site does: NavStepGateService is
        // `providedIn: 'root'`, so its gate map outlives a single navigator session. Without the
        // reset, gates left registered-and-unsatisfied by a run the crew exited mid-step would
        // still be in the map and would block the opening step of the next run — the same
        // cross-session leak as #30, one layer down.
        if (this._gatesRegisteredForIndex === null && this.activeStep) {
            this.gate.reset();
            this._registerStepGates(this.activeStep);
        }

        // Subscribe to gate readiness for confirm button
        this._gateSub = this.gate.canProceed$.subscribe(ok => {
            this.canProceed = ok;
        });

        this._onlineSub = this.onlineStatus.isOnline$.subscribe(online => {
            this.isOnline = online;
        });

        // Must precede `_runTimer`, which reads `_runStartedAt` on its first tick.
        this._seedRunStart();
        this._runTimer();
    }

    ngOnDestroy() {
        // Leaving is not undoing (#31) — and nothing has to be flushed here to make that true
        // any more.
        //
        // This used to hold the pending commit and fire it on the way out, because the advance
        // was not written until the undo timer expired; dropping it meant exiting inside those
        // five seconds silently discarded the step the crew had just finished. Since #46 Stage 2
        // the advance is persisted when Continue is tapped, so exiting simply leaves it written —
        // which is what "leaving is not undoing" always meant.
        clearInterval(this._timerInterval);
        clearInterval(this._undoInterval);

        this._storeSub?.unsubscribe();
        this._gateSub?.unsubscribe();
        this._startTimeSub?.unsubscribe();
        this._onlineSub?.unsubscribe();
    }

    /** The event's persisted `navigatorCurrentStep` as the store currently holds it. */
    private _persistedStepFor(eventId: string, jobId: string): string | null {
        if (!jobId) { return null; }
        const job = this.store.selectSignal(selectJob)();
        return job?.events?.find((e) => e.id === eventId)?.navigatorCurrentStep ?? null;
    }

    /**
     * Register gates for components in this step that have required props.
     * A component is gated when:
     *  - type is 'PhotoCapture' and required=true → needs at least one photo
     *  - type is 'CustomerSignature' and enabled !== false and optional !== true
     *  - type is 'PandaDocSigner' and optional !== true
     *  - type is 'InventoryEditor' (always gated until scope confirmed)
     */
    /**
     * Which `activeIndex` the gate currently holds registrations for, or `null` if none have
     * been registered yet. Lets the store subscription tell "arriving at a step" apart from
     * "the delayed persistence of a confirm we already performed" — see #29.
     */
    private _gatesRegisteredForIndex: number | null = null;

    private _registerStepGates(step: NavNode) {
        this._gatesRegisteredForIndex = this.steps.indexOf(step);

        if (!step?.children) return;
        for (const child of step.children) {
            const p = child.props ?? {};

            if (child.type === 'PhotoCapture' && p.required) {
                this.gate.register(`photo_${p.stepKey ?? child.type}`, true, false);
                p.onReady = (ok: boolean) => this.gate.update(`photo_${p.stepKey ?? child.type}`, ok);
            }

            // Both spellings, to match what the renderer accepts. `navigator.components.tsx`
            // maps this component to the snake_case type, so a script built against those
            // helpers emits `customer_signature` — which rendered fine but registered no gate,
            // because only the capitalised form was checked here.
            if ((child.type === 'CustomerSignature' || child.type === 'customer_signature')
                && p.enabled !== false && !p.optional) {
                this.gate.register(`sig_${p.key ?? 'sig'}`, true, false);
                p.onReady = (ok: boolean) => this.gate.update(`sig_${p.key ?? 'sig'}`, ok);
            }

            if (child.type === 'PandaDocSigner' && !p.optional) {
                this.gate.register(`pandadoc_${p.docType ?? 'doc'}`, true, false);
                p.onReady = (ok: boolean) => this.gate.update(`pandadoc_${p.docType ?? 'doc'}`, ok);
            }

            if (child.type === 'InventoryEditor' || child.type === 'inventory_editor') {
                this.gate.register('inventory_scope', true, false);
                p.onReady = (ok: boolean) => this.gate.update('inventory_scope', ok);
            }

            // A gate whose component can never report back is a dead confirm button: the crew
            // sees the component saying it is satisfied, and no way to advance. Both halves of
            // that failure are silent — `NavStepGateService.update` no-ops on an unregistered
            // key, and a missing `onReady` no-ops in the component — so say something rather
            // than strand them.
            //
            // Checked on `child.props`, not the local `p`: when a node arrives with no props at
            // all, `p` is a throwaway `{}` and the handler above was assigned to an object
            // nothing else can see. That case looks identical from the crew's side and is why
            // this reads through to the node.
            if (ALWAYS_GATED_NODE_TYPES.has(child.type) && typeof child.props?.onReady !== 'function') {
                console.warn(
                    `[Navigator] "${child.type}" is always gated, but no reachable onReady was `
                    + `attached — it can never satisfy its gate, so the confirm button on this `
                    + `step will stay disabled. Either the type name here does not match what `
                    + `the script emits, or the node arrived without a props object.`,
                );
            }

            // CrewRollCall: blocked until all members have a known status (not 'unknown')
            if (child.type === 'CrewRollCall') {
                this.gate.register('crew_checkin', true, false);
                p.onReady = (ok: boolean) => this.gate.update('crew_checkin', ok);
            }

            // HomeProtection: blocked until all checklist items are checked
            if (child.type === 'HomeProtection') {
                this.gate.register('home_protection', true, false);
                p.onReady = (ok: boolean) => this.gate.update('home_protection', ok);
            }

            // LoadProgress: blocked until all rooms are marked done
            if (child.type === 'LoadProgress') {
                this.gate.register('load_progress', true, false);
                p.onReady = (ok: boolean) => this.gate.update('load_progress', ok);
            }

            // Checkbox: every box on the step must be ticked before it can be confirmed, per
            // MOVE_NAVIGATOR_PLAN.md ("order-independent, all must be checked"). One gate per
            // box, keyed on the script's `key` prop so they are independent of each other.
            //
            // Deliberately not persisted (#27): if all boxes must be ticked to advance, then
            // having advanced is the record, and re-ticking after a step re-render costs the
            // crew nothing. That reasoning does not extend to #14/#15, where the lost data is
            // real content that cannot be inferred from progress.
            if (child.type === 'checkbox' || child.type === 'Checkbox') {
                const gateKey = `checkbox_${p.key ?? p.title ?? child.type}`;
                this.gate.register(gateKey, true, false);
                p.onReady = (ok: boolean) => this.gate.update(gateKey, ok);
            }
        }
    }

    get steps(): NavNode[] {
        return (this.node?.children ?? []).filter(c => c.type === 'Step');
    }
    get activeStep(): NavNode | null {
        return this.steps[this.activeIndex] ?? null;
    }
    get isLast(): boolean {
        return this.activeIndex === this.steps.length - 1;
    }
    get progressPct(): number {
        if (this.steps.length <= 1) return 100;
        return Math.round(((this.activeIndex) / (this.steps.length - 1)) * 100);
    }
    /**
     * The step has a component that needs the server, its work is not done, and the phone is
     * offline (#86a). Continue waits for signal rather than queueing: the contract, signature,
     * photos and payment happen on the server as the crew uses them. A weak signal the phone still calls online is not caught
     * here; those components report their own failure, as before.
     */
    get needsSignal(): boolean {
        if (this.isOnline) { return false; }
        // A contract signed or a photo uploaded before the signal dropped is done: its gate is
        // satisfied, and continuing is an ordinary advance that waits in the outbox. The payment
        // has no gate (billing later is a valid answer), so it always waits for signal.
        return (this.activeStep?.children ?? []).some((child) => needsServer(child)
            && (child.type === 'CollectPayment' || !this.canProceed));
    }
    get customerName(): string {
        for (const step of this.steps) {
            for (const child of step.children ?? []) {
                if (child.type === 'CustomerInfo' && child.props?.customerName) {
                    return child.props.customerName;
                }
            }
        }
        return '';
    }
    get activeHelpText(): string {
        const step = this.activeStep;
        if (!step) return '';
        // Check step-level helpText first, then first child with helpText
        if (step.props?.helpText) return step.props.helpText;
        for (const child of step.children ?? []) {
            if (child.props?.helpText) return child.props.helpText;
        }
        return '';
    }

    /**
     * Drives the header timer. Already called from every path that changes the step — mount, the
     * restore subscription, `confirm()` and `undo()` — so the timer needs no wiring of its own.
     *
     * Elapsed is derived from `_stepStartTime`, which is restored from the store rather than
     * stamped at mount, so it resumes correctly after a reload instead of restarting at zero.
     */
    private _runTimer() {
        clearInterval(this._timerInterval);
        const tick = () => {
            const now = Date.now();
            this.stepElapsedSec = Math.max(0, Math.floor((now - this._stepStartTime) / 1000));
            // `_seedRunStart` always assigns, so the null branch is only reachable if a tick beats
            // `ngOnInit` — falling back to the step's own elapsed is the right answer there too.
            this.totalElapsedSec = this._runStartedAt === null
                ? this.stepElapsedSec
                : Math.max(0, Math.floor((now - this._runStartedAt) / 1000));
        };
        tick();
        this._timerInterval = setInterval(tick, 1000);
    }

    /** Sum of every step's `maxTime`. Zero if no step in this script declares one. */
    get totalBudgetSec(): number {
        return this.steps.reduce((sum, step) => sum + (step.props?.maxTime ?? 0), 0);
    }

    /** The current step's budget, or 0 when it has none or opts out of the timer. */
    get stepBudgetSec(): number {
        return this.activeStep?.props?.maxTime ?? 0;
    }

    /** A script can drop the timer for one step with `<Step hideTimer>`. */
    get timerVisible(): boolean {
        return this.activeStep?.props?.hideTimer !== true;
    }

    /**
     * Seed `_runStartedAt` from the earliest `startedAt` already persisted, so the whole-job total
     * survives a reload mid-run. One-shot: see the field's note on why this is not a subscription.
     */
    private _seedRunStart() {
        const eventId = this.route.snapshot.paramMap.get('eventId');
        const job = this.store.selectSignal(selectJob)();
        const event = job?.events?.find((e: any) => e.id === eventId);

        let steps: Record<string, StepTiming> | undefined;
        try {
            steps = event?.navigatorData ? JSON.parse(event.navigatorData)?.steps : undefined;
        } catch { /* fall through to the current step's start */ }

        let earliest: number | null = null;
        for (const timing of Object.values(steps ?? {})) {
            const startedAt = timing?.startedAt;
            if (startedAt != null && (earliest === null || startedAt < earliest)) {
                earliest = startedAt;
            }
        }

        // Falling back to the current step's start is what makes a **fresh run** work, and getting
        // this wrong is how the whole-job total shipped broken on 2026-08-18.
        //
        // The first version returned early whenever `navigatorData` had no `steps` key — which is
        // the normal state before the first confirm — leaving `_runStartedAt` null. This method runs
        // once, in `ngOnInit`, so it was never re-seeded: null persisted for the entire session, and
        // the tick's null-guard fell back to `totalElapsedSec = stepElapsedSec`. Both timers then
        // counted from the step start, for the whole run.
        //
        // On a fresh run the current step's start *is* when the job started, so this is correct
        // rather than merely defensive. Assigned unconditionally: every path must leave a value.
        this._runStartedAt = earliest ?? this._stepStartTime;
    }

    /**
     * Shows the "Back to Step N" affordance for five seconds. **Presentation only.**
     *
     * It used to hold the pending persistence: the advance was written when this timer expired,
     * which left the database five seconds behind the crew. Every whole-job websocket echo
     * arriving in that gap carried the *previous* step and walked them backwards — #46, and the
     * fifth bug traceable to this window after #29 and #31a/b/c. The advance is now written the
     * moment Continue is tapped, and `undo()` reverses it, so nothing depends on this timer
     * beyond the button's visibility.
     */
    private _startUndoWindow() {
        clearInterval(this._undoInterval);
        this.undoVisible = true;
        this.undoSecondsLeft = UNDO_DURATION_S;
        this.undoProgress = 100;

        const tick = () => {
            this.undoSecondsLeft--;
            this.undoProgress = (this.undoSecondsLeft / UNDO_DURATION_S) * 100;
            if (this.undoSecondsLeft <= 0) {
                clearInterval(this._undoInterval);
                this.undoVisible = false;
            }
        };
        this._undoInterval = setInterval(tick, 1000);
    }

    confirm(step: NavNode) {
        if (step.props?.confirmButtonEnabled === false) return;
        if (this.undoVisible) return;
        if (!this.canProceed) return;
        if (this.needsSignal) return;

        const eventId = this.route.snapshot.paramMap.get('eventId') ?? '';
        const stepStartTime = Date.now();
        const previousStepId = step.props?.id ?? '';
        const previousStepCompletedAt = stepStartTime;
        const previousStepStartedAt = this._stepStartTime;

        if (!this.isLast) {
            this.activeIndex++;
            this._stepStartTime = stepStartTime;
            this._runTimer();
            this.gate.reset();
            this._registerStepGates(this.activeStep!);

            const nextStepId = this.activeStep?.props?.id ?? '';
            const jobId = this.store.selectSignal(selectJobId)() ?? '';

            // Move the store's step now rather than when the undo window closes (#31).
            //
            // Safe only because of #29: this patch makes the restore subscription re-emit the
            // step we are already standing on, and #29's `_gatesRegisteredForIndex` guard is
            // what stops that from re-registering the gates and discarding the crew's answers.
            this._stepBeforeOptimisticAdvance = this._persistedStepFor(eventId, jobId);
            const previousPersistedStep = this._stepBeforeOptimisticAdvance;

            // The returned-to step's real start time, kept so `undo()` can restore it instead of
            // re-stamping `Date.now()`. Without this the crew's time on that step before the
            // mis-tap vanishes from its reported duration, and the undo would leave the database
            // differing from the pre-confirm state — see `navigatorStepUndone`'s invariant.
            this._stepStartTimeBeforeAdvance = previousStepStartedAt;

            if (jobId) {
                this.store.dispatch(WorkOrdersActions.navigatorStepAdvancedLocally({
                    jobId, eventId, navigatorCurrentStep: nextStepId,
                }));
            }

            // Persist immediately — NOT when the undo window closes (#46 Stage 2).
            //
            // This used to be the undo timer's callback, which left the database five seconds
            // behind the crew. Any whole-job websocket echo arriving in that gap carried the
            // *previous* step and walked them backwards; only two of the six `emitJobChanged`
            // callers even pass an `eventId`, so most echoes cannot be corrected server-side.
            // Guarding the window (1a/1b) narrowed it but could not close it — the write had to
            // stop being deferred. `undo()` now reverses this rather than pre-empting it.
            this.store.dispatch(quickJsActions.navigatorStepConfirmed({
                stepId: nextStepId,
                eventId,
                jobId,
                stepStartTime,
                previousStepId,
                previousStepCompletedAt,
                previousStepStartedAt,
                previousPersistedStep,
            }));

            this._startUndoWindow();
        } else {
            // Last step — read the stored entry for the last step, to keep its start time.
            //
            // Same hand-rolled router walk and string-keyed Jobs reach as in
            // NavMovePerformanceSummaryComponent above; `selectJob` would replace both.
            // Suppressed rather than left failing, and tracked in #8.
            let existingNavData: Record<string, any> = {};
            // eslint-disable-next-line @ngrx/prefer-selector-in-select
            this.store.select((s: any) => {
                const walk = (n: any): string | null => n?.params?.jobId || walk(n?.firstChild);
                const jobId = walk(s?.router?.state?.root);
                return s?.['Jobs']?.jobs?.[jobId]?.events?.find((e: any) => e.id === eventId)?.navigatorData;
            // eslint-disable-next-line @ngrx/no-store-subscription
            }).subscribe((raw: string | null) => {
                try { existingNavData = raw ? JSON.parse(raw) : {}; } catch { /* */ }
            }).unsubscribe();

            // The last step only: the server merges it into the stored run (#48) and reads the real
            // end from the merged result.
            const storedLastStep = existingNavData.steps?.[previousStepId];
            const finalNavData = JSON.stringify({
                stepStartTime,
                steps: {
                    [previousStepId]: {
                        ...(storedLastStep ?? {}),
                        startedAt: previousStepStartedAt ?? storedLastStep?.startedAt ?? null,
                        completedAt: previousStepCompletedAt,
                    },
                },
            });

            this.store.dispatch(quickJsActions.navigatorCompleted({
                eventId,
                lastStepId: previousStepId,
                lastStepStartedAt: previousStepStartedAt ?? stepStartTime,
                lastStepCompletedAt: previousStepCompletedAt,
                navigatorData: finalNavData,
            }));
        }
    }

    undo() {
        if (this.activeIndex > 0) {
            clearInterval(this._undoInterval);
            this.undoVisible = false;

            const jobId = this.store.selectSignal(selectJobId)() ?? '';
            const eventId = this.route.snapshot.paramMap.get('eventId') ?? '';
            const abandonedStepId = this.activeStep?.props?.id ?? '';
            const returnedStepId = this.steps[this.activeIndex - 1]?.props?.id ?? '';

            // Restore the step's original start time rather than re-stamping `Date.now()`, so the
            // timer picks up where it left off and the persisted duration keeps the time the crew
            // actually spent here before the mis-tap. The fallback only matters if an advance was
            // never recorded, in which case there is nothing to restore.
            const restoredStepStartTime = this._stepStartTimeBeforeAdvance ?? Date.now();

            // Nothing to adjust for the whole-job total: it is wall clock from `_runStartedAt`, and
            // an undo does not give the crew back the time they spent. See `_runStartedAt`.
            this.activeIndex--;
            this._stepStartTime = restoredStepStartTime;
            this._runTimer();

            // The advance is already in the database (#46 Stage 2), so undo has to un-write it
            // rather than cancel a pending commit. `navigatorStepUndone` restores `currentStep`,
            // puts the returned step back to in-progress with its original start, and deletes the
            // abandoned step's entry outright — see that action for the full invariant.
            //
            // The local patch it used to do here is now the effect's job, because the store and
            // the database have to end up agreeing; patching only the store would recreate the
            // divergence Stage 2 exists to remove.
            if (jobId && eventId && returnedStepId) {
                this.store.dispatch(quickJsActions.navigatorStepUndone({
                    stepId: returnedStepId,
                    eventId,
                    jobId,
                    abandonedStepId,
                    restoredStepStartTime,
                }));
            }

            this._stepBeforeOptimisticAdvance = null;
            this._stepStartTimeBeforeAdvance = null;

            // Re-point the gate at the step we just returned to. Every other path that moves
            // between steps does this — confirm() below, and both branches of the restore
            // subscription — and undo() was the one that did not, which broke it in both
            // directions (#25): the step you left kept its gate registered, so the step you
            // landed on was permanently unconfirmable; and a step you returned *to* never had
            // its own gate registered, so it could be advanced past without being satisfied.
            // That second case silently skipped crew check-in, inventory confirmation and
            // signatures.
            this.gate.reset();
            this._registerStepGates(this.activeStep!);
        }
    }

    exit() {
        const segments = this.router.url.split('/');
        const navigatorIdx = segments.indexOf('navigator');
        if (navigatorIdx >= 0) {
            this.router.navigateByUrl(segments.slice(0, navigatorIdx).join('/') + '/workorders');
        } else {
            this.router.navigate(['/']);
        }
    }
}
